import { ShoppingCart, ChevronDown } from 'lucide-react';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import RecipeModal from '../components/RecipeModal';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, BookOpen, Calculator, Trash2, Edit, DollarSign, Package, AlertTriangle, Copy as CopyIcon, Heart, Printer } from 'lucide-react';
import { useForm, useWatch, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import api, { recipesAPI, firearmsAPI, componentsAPI, cartridgesAPI, loadsAPI, uploadsAPI, firingSessionsAPI, usersAPI, conversationsAPI, purchasesAPI, cartridgeAbbrev, cartridgesStatic, caliberOptionsAPI } from '../services/api';
import { formatLocalDate } from '../services/date';

// Debounce helper for event handlers
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const Recipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [firearms, setFirearms] = useState([]);
  const [componentsByType, setComponentsByType] = useState({});
  const [cartridges, setCartridges] = useState([]);
  const [loads, setLoads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [velocityStats, setVelocityStats] = useState({});

  const fetchVelocityStats = async (recipesList) => {
    if (!Array.isArray(recipesList) || recipesList.length === 0) {
      setVelocityStats({});
      return;
    }
    try {
      const ids = recipesList.map((r) => r.id);
      const res = await recipesAPI.getVelocityStats(ids);
      setVelocityStats(res?.data || {});
    } catch (error) {
      console.error('Failed to load velocity stats', error);
      setVelocityStats({});
    }
  };
  const [recipeStats, setRecipeStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [componentAvailability, setComponentAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [imagesByRecipe, setImagesByRecipe] = useState({});
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseComponent, setPurchaseComponent] = useState(null);
  const [purchaseForm, setPurchaseForm] = useState({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '', starting_firing_count: '0' });
  const [showAddLoadsFromRecipe, setShowAddLoadsFromRecipe] = useState(false);
  const [showSessionFromRecipe, setShowSessionFromRecipe] = useState(false);
  const [showShareRecipe, setShowShareRecipe] = useState(false);
  const [recipeCaseBatches, setRecipeCaseBatches] = useState([]);
  const [fieldsLocked, setFieldsLocked] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  const buildPrintHtml = (recipe, targetUrl, qrSrc) => {
    const title = recipe.is_deleted ? `Deleted — ${recipe.name}` : recipe.name;
    return `
<!doctype html>
<html>
  <head>
    <meta charset="utf-8">
    <title>${title}</title>
    <style>
      html, body { background: #fff; margin: 0; padding: 0; }
      .wrap { width: 700px; margin: 24px auto; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; }
      .card { border: 1px solid #e5e7eb; border-radius: 8px; padding: 24px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
      h1 { font-size: 20px; text-align: center; margin: 0 0 6px; }
      p { margin: 0; text-align: center; color: #6b7280; font-size: 12px; }
      .qr { display: flex; align-items: center; justify-content: center; margin-top: 12px; }
      .qr img, .qr canvas { width: 256px; height: 256px; border: 1px solid #e5e7eb; }
      .url { margin-top: 10px; text-align: center; font-size: 11px; color: #6b7280; word-break: break-all; }
      @page { size: auto; margin: 0.5in; }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="card">
        <h1>${title}</h1>
        <p>Scan to open this recipe</p>
        <div class="qr">
          <img id="qr" src="${qrSrc}" alt="QR Code"/>
        </div>
        <div class="url">${targetUrl}</div>
      </div>
    </div>
    <script>
      (function(){
        function go(){ setTimeout(function(){ try{ window.focus(); window.print(); }catch(e){} }, 100); }
        var img = document.getElementById('qr');
        if (!img) { go(); return; }
        if (img.complete) { go(); } else { img.onload = go; img.onerror = go; }
      })();
    </script>
  </body>
</html>`;
  };

  const handlePrintRecipe = (recipe) => {
    try {
      const origin = window.location.origin;
      const targetUrl = `${origin}/recipes?recipeId=${encodeURIComponent(recipe.id)}`;
      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(targetUrl)}&margin=1`;
      const html = buildPrintHtml(recipe, targetUrl, qrSrc);
      const ua = (navigator?.userAgent || '').toLowerCase();
      const isMobile = /android|iphone|ipad|ipod/.test(ua);
      if (isMobile) {
        const w = window.open('', 'recipe-print', 'width=800,height=600');
        if (!w) {
          throw new Error('popup blocked');
        }
        w.document.write(html);
        w.document.close();
        return;
      }
      // Use a hidden iframe so we don't navigate away
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow || iframe.contentDocument;
      const iw = iframe.contentWindow;
      const idoc = doc.document || doc;
      idoc.open();
      idoc.write(html);
      idoc.close();
      // Cleanup after print
      const remove = () => { try { document.body.removeChild(iframe); } catch {} };
      if (iw) {
        iw.onafterprint = remove;
        // Fallback cleanup after some time
        setTimeout(remove, 5000);
      } else {
        setTimeout(remove, 3000);
      }
    } catch {
      // Fallback: open a small popup and let it auto-print
      try {
        const origin2 = window.location.origin;
        const targetUrl2 = `${origin2}/recipes?recipeId=${encodeURIComponent(recipe.id)}`;
        const qrSrc2 = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(targetUrl2)}&margin=1`;
        const html2 = buildPrintHtml(recipe, targetUrl2, qrSrc2);
        const w = window.open('', 'recipe-print', 'width=800,height=600');
        if (!w) return;
        w.document.write(html2);
        w.document.close();
      } catch {}
    }
  };
  
  const location = useLocation();
  const navigate = useNavigate();
  const canDeleteRecipe = (recipeId) => {
    const relatedLoads = (loads || []).filter(l => l.recipe?.id === recipeId);
    if (relatedLoads.length === 0) return true; // hard delete allowed
    const remaining = relatedLoads.reduce((sum, l) => sum + (l.remaining_quantity || 0), 0);
    return remaining === 0; // soft delete allowed (no remaining)
  };
  const deleteRecipe = async (recipe) => {
    if (!canDeleteRecipe(recipe.id)) return;
    if (!window.confirm('Delete this recipe? If loads exist, it will be hidden but retained for history.')) return;
    try {
      await recipesAPI.remove(recipe.id);
      toast.success('Recipe deleted');
      // Incremental update: remove or mark as deleted in local state
      setRecipes(prev => prev.filter(r => String(r.id) !== String(recipe.id)));
      // Also remove from stats
      setRecipeStats(prev => {
        const next = { ...prev };
        delete next[recipe.id];
        return next;
      });
      // Remove from images
      setImagesByRecipe(prev => {
        const next = { ...prev };
        delete next[recipe.id];
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to delete recipe');
    }
  };
  const [filters, setFilters] = useState({
    cartridge: [],
    caseBrand: [],
    caseHeadstamp: [],
    bulletBrand: [],
    bulletModel: [],
    bulletWeight: [],
    bulletMaterial: [],
    bulletCovering: [],
    bulletShape: [],
    primerBrand: [],
    primerModel: [],
    primerFirearmType: [],
    primerSize: [],
    primerMagnum: [],
    powderBrand: [],
    powderModel: [],
    favorites: []
  });
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [sortBy, setSortBy] = useState({
    field: 'name',
    direction: 'asc'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm({
    defaultValues: { crimp_strength: 1 }
  });
  
  // Watch form fields for conditional rendering
  const selectedBrass = useWatch({ control, name: 'brass_id' });
  const selectedBullet = useWatch({ control, name: 'bullet_id' });
  const selectedPrimer = useWatch({ control, name: 'primer_id' });
  const selectedPowder = useWatch({ control, name: 'powder_id' });
  const cartridge = useWatch({ control, name: 'cartridge' });
  const cartridgeOther = useWatch({ control, name: 'cartridge_other' });
  const effectiveCartridge = (cartridge === 'other' ? (cartridgeOther || '') : (cartridge || '')).trim();
  const selfCannelured = useWatch({ control, name: 'self_cannelured' });
  const caseTrimLength = useWatch({ control, name: 'case_trim_length' });
  const cartridgeOverallLength = useWatch({ control, name: 'cartridge_overall_length' });
  const seatingDepth = useWatch({ control, name: 'seating_depth' });
  const powderCharge = useWatch({ control, name: 'powder_charge' });

  // Auto-generate recipe name for create form until user edits it
  const [isNameAuto, setIsNameAuto] = useState(true);
  const lastAutoNameRef = useRef('');
  const recipeFormRef = useRef(null);

  useEffect(() => {
    if (!showForm || editingRecipe) return; // only for create form
    // Build suggested name parts
    const findComp = (type, id) => {
      if (!id) return null;
      try {
        const list = componentsByType[type] || [];
        const pid = parseInt(id);
        return list.find(x => x && parseInt(x.id) === pid) || null;
      } catch {
        return null;
      }
    };
    const brass = findComp('brass', selectedBrass);
    const bullet = findComp('bullet', selectedBullet);
    const primer = findComp('primer', selectedPrimer);
    const powder = findComp('powder', selectedPowder);

    const parts = [];
    const cartLabel = (typeof cartridgeAbbrev === 'function' ? (cartridgeAbbrev(effectiveCartridge || '') || effectiveCartridge) : effectiveCartridge);
    if (cartLabel) parts.push(cartLabel);
    if (brass?.brand) parts.push(brass.brand);
    if (bullet?.brand) parts.push(bullet.brand);
    if (bullet?.weight_grains) parts.push(`${bullet.weight_grains}`);
    // omit bullet core material per request
    if (bullet?.covering) parts.push(bullet.covering);
    if (bullet?.shape) parts.push(bullet.shape);
    if (primer) {
      const sizeRaw = (primer.primer_size || '').toLowerCase(); // "small" | "large"
      const typeRaw = (primer.firearm_type || '').toLowerCase(); // "pistol" | "rifle"
      const mag = !!primer.magnum;
      const sizeAbbr = sizeRaw === 'small' ? 'S' : (sizeRaw === 'large' ? 'L' : '');
      const typeAbbr = typeRaw === 'pistol' ? 'P' : (typeRaw === 'rifle' ? 'R' : '');
      const base = `${sizeAbbr}${typeAbbr}`;
      const primerAbbr = base ? `${base}${mag ? 'M' : ''}` : '';
      if (primerAbbr) parts.push(primerAbbr);
    }
    if (powder?.model) parts.push(powder.model);
    if (powderCharge) parts.push(`${powderCharge}`);

    const autoName = parts.join(' ').replace(/\s+/g, ' ').trim();
    if (isNameAuto) {
      setValue('name', autoName);
      lastAutoNameRef.current = autoName;
    } else {
      // If user cleared or name equals previous auto, keep syncing until diverges
      const currentName = watch('name');
      if (!currentName || currentName === lastAutoNameRef.current) {
        setValue('name', autoName);
        lastAutoNameRef.current = autoName;
      }
    }
  }, [showForm, editingRecipe, effectiveCartridge, selectedBrass, selectedBullet, selectedPrimer, selectedPowder, powderCharge, componentsByType, setValue, watch, isNameAuto]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  // Incremental update for firing session changes - only refresh loads and stats, not everything
  const handleFiringSessionChange = useCallback(async (event) => {
    const detail = event?.detail || {};
    const { recipeId, loadId } = detail;
    
    try {
      // Only fetch loads (which includes remaining quantities) - much faster than full loadData()
      const loadsRes = await loadsAPI.getAll();
      const newLoads = Array.isArray(loadsRes.data) ? loadsRes.data : [];
      setLoads(newLoads);
      
      // Only recalculate stats for the affected recipe if we know which one
      if (recipeId) {
        const affectedRecipe = recipes.find(r => String(r.id) === String(recipeId));
        if (affectedRecipe) {
          // Calculate stats just for this recipe
          const recipeLoads = newLoads.filter(l => {
            const lrid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
            return String(lrid) === String(recipeId);
          });
          const totalLoadsCreated = recipeLoads.reduce((sum, load) => sum + (Number(load.quantity_created) || 0), 0);
          const totalLoadsRemaining = recipeLoads.reduce((sum, load) => sum + (Number(load.remaining_quantity) || 0), 0);
          let totalSpent = recipeLoads.reduce((sum, load) => {
            const tc = Number(load.total_cost);
            if (!isNaN(tc) && tc > 0) return sum + tc;
            const cpr = Number(load.cost_per_round);
            const qty = Number(load.quantity_created);
            if (!isNaN(cpr) && !isNaN(qty)) return sum + (cpr * qty);
            return sum;
          }, 0);
          const averageCostPerLoad = totalLoadsCreated > 0 ? totalSpent / totalLoadsCreated : null;
          
          setRecipeStats(prev => ({
            ...prev,
            [recipeId]: {
              ...prev[recipeId],
              totalLoadsCreated,
              totalLoadsRemaining,
              totalSpent,
              averageCostPerLoad,
            }
          }));
        }
      } else {
        // Fallback: recalculate all stats (still faster than full loadData)
        await calculateRecipeStats(recipes, newLoads);
      }
      
      // Refresh sessions for the modal if it's open
      if (showRecipeModal && selectedRecipe) {
        const sessionsRes = await firingSessionsAPI.getAll();
        setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
      }
      
      // Refresh velocity stats for the affected recipe (or all if recipeId unknown)
      if (recipeId) {
        try {
          const vsRes = await recipesAPI.getVelocityStats([recipeId]);
          const vsData = vsRes?.data || {};
          setVelocityStats(prev => ({ ...prev, ...vsData }));
        } catch {}
      } else {
        // Fallback: refresh all velocity stats
        await fetchVelocityStats(recipes);
      }
    } catch (error) {
      console.error('Error handling firing session change:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipes, showRecipeModal, selectedRecipe]);

  // Debounced version to prevent rapid-fire updates
  const debouncedSessionChangeRef = useRef(null);
  useEffect(() => {
    debouncedSessionChangeRef.current = debounce(handleFiringSessionChange, 300);
  }, [handleFiringSessionChange]);

  useEffect(() => {
    const handler = (event) => {
      if (debouncedSessionChangeRef.current) {
        debouncedSessionChangeRef.current(event);
      }
    };
    window.addEventListener('firingSessionChanged', handler);
    return () => window.removeEventListener('firingSessionChanged', handler);
  }, []);

  // Listen for uploads changed event (when files are uploaded from other pages like FiringSessions/Loads)
  useEffect(() => {
    const handleUploadsChanged = async (event) => {
      const { recipeId, entity, id } = event?.detail || {};
      if (!recipeId) return;
      
      // Refresh images for the affected recipe
      try {
        const [loadsRes, sessionsRes] = await Promise.all([loadsAPI.getAll(), firingSessionsAPI.getAll()]);
        const loadsList = Array.isArray(loadsRes.data) ? loadsRes.data : [];
        const sessionsList = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
        
        // Map ALL batch_ids to recipeId (loads are grouped, sessions reference specific IDs)
        const loadIdToRecipeId = new Map();
        loadsList.forEach(l => {
          const rid = (l?.recipe?.id) || l?.recipe_id;
          const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
          ids.forEach(lid => loadIdToRecipeId.set(lid, rid));
        });
        
        // Collect all load IDs for the recipe (include all batch_ids)
        const loadIds = [];
        loadsList.forEach(l => {
          if (String((l?.recipe?.id) || l?.recipe_id) === String(recipeId)) {
            const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
            ids.forEach(lid => loadIds.push(lid));
          }
        });
        
        const sessionIds = sessionsList.filter(s => {
          const loadId = (s?.load?.id) || s?.load_id;
          const rid = loadIdToRecipeId.get(loadId);
          return String(rid) === String(recipeId);
        }).map(s => s.id);
        
        const queries = [
          { entity_type: 'recipe', entity_id: recipeId },
          ...loadIds.map(lid => ({ entity_type: 'load', entity_id: lid })),
          ...sessionIds.map(sid => ({ entity_type: 'firing_session', entity_id: sid })),
        ];
        const batchRes = await uploadsAPI.batchList(queries);
        const batchData = batchRes?.data || {};
        
        const merged = [];
        const seen = new Set();
        Object.values(batchData).forEach(arr => {
          (arr || []).forEach(img => {
            if (img && !seen.has(img.id)) {
              seen.add(img.id);
              merged.push(img);
            }
          });
        });
        
        setImagesByRecipe(prev => ({ ...prev, [recipeId]: merged }));
      } catch (err) {
        console.error('Error refreshing images after upload:', err);
      }
    };
    
    window.addEventListener('uploadsChanged', handleUploadsChanged);
    return () => window.removeEventListener('uploadsChanged', handleUploadsChanged);
  }, []);

  // Open modal directly if URL contains recipeId (for deep-linking from Firearms tab)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const rid = params.get('recipeId');
    if (!rid) return;
    const found = recipes.find(r => String(r.id) === String(rid));
    if (found) {
      setSelectedRecipe(found);
      setShowRecipeModal(true);
    }
  }, [location.search, recipes]);

  // Keep selectedRecipe in sync with recipes array (for when recipes are updated after edits)
  useEffect(() => {
    if (selectedRecipe && recipes.length > 0) {
      const updated = recipes.find(r => r.id === selectedRecipe.id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(selectedRecipe)) {
        setSelectedRecipe(updated);
      }
    }
  }, [recipes, selectedRecipe]);

  // Prevent clearing during edit prefill
  const isPrefillingRef = useRef(false);
  // When cartridge changes, clear dependent component selections
  useEffect(() => {
    if (isPrefillingRef.current) return;
    setValue('brass_id', '');
    setValue('bullet_id', '');
    setValue('primer_id', '');
  }, [effectiveCartridge, setValue]);

  // When brass changes, clear primer selection (skip during edit prefill)
  useEffect(() => {
    if (isPrefillingRef.current) return;
    setValue('primer_id', '');
  }, [selectedBrass, setValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recipesRes, firearmsRes, componentsRes, caliberOptionsRes, loadsRes, sessionsRes] = await Promise.all([
        recipesAPI.getAll(),
        firearmsAPI.getAll(),
        componentsAPI.getForRecipe(),
        caliberOptionsAPI.getAll(),
        loadsAPI.getAll(),
        firingSessionsAPI.getAll()
      ]);
      setRecipes(recipesRes.data);
      setFirearms(firearmsRes.data);
      setComponentsByType(componentsRes.data);
      // Merge static list with custom cartridges from backend
      const staticList = cartridgesStatic();
      const normalize = (v) => (v || '').trim().replace(/^\./, '').trim();
      const staticSet = new Set(staticList.map(s => (s.value || '').trim().toLowerCase()));
      const customList = (caliberOptionsRes.data || [])
        .map(o => ({ id: `custom-${o.id}`, value: normalize(o.value) }))
        .filter(o => o.value && !o.value.startsWith('.'));
      const seen = new Set();
      const merged = [];
      [...staticList, ...customList].forEach(o => {
        const key = normalize(o.value).toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        if (staticSet.has(key)) {
          const found = staticList.find(s => (s.value || '').trim().toLowerCase() === key);
          if (found) { merged.push(found); return; }
        }
        merged.push({ id: o.id, value: normalize(o.value) });
      });
      merged.sort((a,b)=> (a.value||'').localeCompare(b.value||'', undefined, {sensitivity:'base', numeric:true}));
      setCartridges(merged);
      setLoads(loadsRes.data);
      setSessions(sessionsRes.data);
      await fetchVelocityStats(recipesRes.data);
      
      // Calculate recipe statistics
      await calculateRecipeStats(recipesRes.data, loadsRes.data);
      // Load aggregated images by recipe using batch API (single call instead of N*M calls)
      try {
        const recipesList = Array.isArray(recipesRes.data) ? recipesRes.data : [];
        const loadsList = Array.isArray(loadsRes.data) ? loadsRes.data : [];
        const sessionsList = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
        
        // Build a map of loadId -> recipeId for session lookups
        // Must include ALL batch_ids since loads are grouped, but sessions reference specific load IDs
        const loadIdToRecipeId = new Map();
        loadsList.forEach(l => {
          const rid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
          // Map all batch IDs to this recipe (handles grouped loads)
          const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
          ids.forEach(id => loadIdToRecipeId.set(id, rid));
        });
        
        // Collect all queries for the batch API
        const queries = [];
        const recipeToEntities = new Map(); // recipeId -> { loadIds: Set, sessionIds: Set }
        
        recipesList.forEach(r => {
          queries.push({ entity_type: 'recipe', entity_id: r.id });
          recipeToEntities.set(r.id, { loadIds: new Set(), sessionIds: new Set() });
        });
        
        // Map loads to their recipes
        loadsList.forEach(l => {
          const lrid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
          const entry = recipeToEntities.get(lrid);
          if (entry) {
            const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
            ids.forEach(id => {
              entry.loadIds.add(id);
              queries.push({ entity_type: 'load', entity_id: id });
            });
          }
        });
        
        // Map sessions to their recipes (via load)
        sessionsList.forEach(s => {
          const loadId = (s && s.load && s.load.id) || s?.load_id;
          const rid = loadIdToRecipeId.get(loadId);
          const entry = recipeToEntities.get(rid);
          if (entry) {
            entry.sessionIds.add(s.id);
            queries.push({ entity_type: 'firing_session', entity_id: s.id });
          }
        });
        
        // Single batch API call
        const batchRes = await uploadsAPI.batchList(queries);
        const batchData = batchRes?.data || {};
        
        // Aggregate images by recipe
        const imagesMap = {};
        recipesList.forEach(r => {
          const rId = r.id;
          const entry = recipeToEntities.get(rId);
          const merged = [];
          const seen = new Set();
          
          // Add recipe images
          const recipeImgs = batchData[`recipe:${rId}`] || [];
          recipeImgs.forEach(img => { if (img && !seen.has(img.id)) { seen.add(img.id); merged.push(img); } });
          
          // Add load images
          if (entry) {
            entry.loadIds.forEach(loadId => {
              const loadImgs = batchData[`load:${loadId}`] || [];
              loadImgs.forEach(img => { if (img && !seen.has(img.id)) { seen.add(img.id); merged.push(img); } });
            });
            
            // Add session images
            entry.sessionIds.forEach(sessionId => {
              const sessionImgs = batchData[`firing_session:${sessionId}`] || [];
              sessionImgs.forEach(img => { if (img && !seen.has(img.id)) { seen.add(img.id); merged.push(img); } });
            });
          }
          
          imagesMap[rId] = merged;
        });
        
        setImagesByRecipe(imagesMap);
      } catch {}
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCalc = (field, value) => {
    const quantity = parseFloat(field === 'quantity' ? value : purchaseForm.quantity) || 0;
    let next = { ...purchaseForm, [field]: value };
    if (field === 'total_cost' || field === 'quantity') {
      const calcUnit = quantity > 0 ? ((parseFloat(next.total_cost)||0) / quantity) : 0;
      next.unit_cost = isFinite(calcUnit) ? calcUnit.toFixed(2) : '0.00';
    }
    if (field === 'unit_cost' || field === 'quantity') {
      const calcTotal = (parseFloat(next.unit_cost)||0) * quantity;
      next.total_cost = isFinite(calcTotal) ? calcTotal.toFixed(2) : '0.00';
    }
    setPurchaseForm(next);
  };

  const submitPurchaseFromRecipe = async () => {
    try {
      if (!purchaseComponent) return;
      await purchasesAPI.create({
        component_id: purchaseComponent.id,
        quantity: parseFloat(purchaseForm.quantity)||0,
        unit: purchaseComponent.component_type?.name?.toLowerCase()==='powder' ? 'pounds' : 'pieces',
        unit_cost: parseFloat(purchaseForm.unit_cost)||0,
        total_cost: parseFloat(purchaseForm.total_cost)||0,
        purchase_date: purchaseForm.purchase_date,
        purchase_location: purchaseForm.purchase_location||'',
        website: purchaseForm.website||'',
        notes: purchaseForm.notes||'',
        starting_firing_count: (purchaseComponent.component_type?.name?.toLowerCase()==='brass') ? (parseInt(purchaseForm.starting_firing_count)||0) : undefined
      });
      toast.success('Purchase recorded');
      setShowPurchaseModal(false);
      // Refresh availability in the open recipe modal
      if (selectedRecipe) {
        setLoadingAvailability(true);
        try {
          const availability = await getComponentAvailability(selectedRecipe.id);
          setComponentAvailability(availability);
          // Refresh case batch dropdown as well so firing counts reflect new purchase
          try {
            const resBatches = await loadsAPI.getCaseBatches(selectedRecipe.id);
            setRecipeCaseBatches(Array.isArray(resBatches.data) ? resBatches.data : []);
          } catch {}
        } finally {
          setLoadingAvailability(false);
        }
      }
    } catch {
      toast.error('Failed to record purchase');
    }
  };

  // Per-firearm velocity stats for a recipe using available sessions
  const getVelocityByFirearmForRecipe = (recipeId) => {
    const key = String(recipeId);
    return velocityStats[key] || [];
  };

  // Calculate the third dimension field when two are entered
  useEffect(() => {
    if (!selectedBullet || !componentsByType.bullet) return;
    
    const bullet = componentsByType.bullet?.find(b => b.id === parseInt(selectedBullet));
    if (!bullet?.length_inches) return;
    
    const bulletLength = bullet.length_inches;
    const trimLength = parseFloat(caseTrimLength) || 0;
    const overallLength = parseFloat(cartridgeOverallLength) || 0;
    const seating = parseFloat(seatingDepth) || 0;
    
    // Check if each field has a valid numeric value
    const hasTrimLength = caseTrimLength && !isNaN(parseFloat(caseTrimLength));
    const hasOverallLength = cartridgeOverallLength && !isNaN(parseFloat(cartridgeOverallLength));
    const hasSeatingDepth = seatingDepth && !isNaN(parseFloat(seatingDepth));
    
    // Count how many fields have values
    const filledFields = [hasTrimLength, hasOverallLength, hasSeatingDepth].filter(Boolean).length;
    
    // Calculate if exactly 2 fields are filled, or if bullet changed and we have 2+ fields filled
    if (filledFields === 2) {
      // Calculate the missing field
      if (hasTrimLength && hasOverallLength && !hasSeatingDepth) {
        // Calculate seating depth: bullet length - (cartridge overall length - case trim length)
        const calculatedSeating = bulletLength - (overallLength - trimLength);
        setValue('seating_depth', calculatedSeating.toFixed(3));
      } else if (hasTrimLength && hasSeatingDepth && !hasOverallLength) {
        // Calculate overall length: bullet length - seating depth + case trim length
        const calculatedOverall = bulletLength - seating + trimLength;
        setValue('cartridge_overall_length', calculatedOverall.toFixed(3));
      } else if (hasOverallLength && hasSeatingDepth && !hasTrimLength) {
        // Calculate trim length: cartridge overall length + seating depth - bullet length
        const calculatedTrim = overallLength + seating - bulletLength;
        setValue('case_trim_length', calculatedTrim.toFixed(3));
      }
    } else if (filledFields >= 2) {
      // If bullet changed and we have 2+ fields filled, recalculate the most recently calculated field
      // This is a heuristic - we'll recalculate the field that was most likely calculated
      if (hasTrimLength && hasOverallLength && hasSeatingDepth) {
        // All three fields have values, recalculate seating depth (most common calculated field)
        const calculatedSeating = bulletLength - (overallLength - trimLength);
        setValue('seating_depth', calculatedSeating.toFixed(3));
      }
    }
  }, [caseTrimLength, cartridgeOverallLength, seatingDepth, selectedBullet, setValue, componentsByType.bullet]);

  // Auto-populate cannelure depth for cannelured bullets
  useEffect(() => {
    if (!selectedBullet) return;
    
    const bullet = componentsByType.bullet?.find(b => b.id === parseInt(selectedBullet));
    if (bullet?.cannelured && bullet?.cannelure_depth) {
      setValue('cannelure_depth', bullet.cannelure_depth.toFixed(3));
    }
  }, [selectedBullet, componentsByType.bullet, setValue]);

  const calculateRecipeStats = async (recipesData, loadsData) => {
    const stats = {};
    
    for (const recipe of recipesData) {
      // Get loads for this recipe
      const recipeLoads = (loadsData || []).filter(load => {
        const lrid = (load && load.recipe && load.recipe.id) || load?.recipe_id;
        return String(lrid) === String(recipe.id);
      });
      
      // Calculate basic stats with robust fallbacks
      const totalLoadsCreated = recipeLoads.reduce((sum, load) => sum + (Number(load.quantity_created) || 0), 0);
      const totalLoadsRemaining = recipeLoads.reduce((sum, load) => sum + (Number(load.remaining_quantity) || 0), 0);
      let totalSpent = recipeLoads.reduce((sum, load) => {
        const tc = Number(load.total_cost);
        if (!isNaN(tc) && tc > 0) return sum + tc;
        const cpr = Number(load.cost_per_round);
        const qty = Number(load.quantity_created);
        if (!isNaN(cpr) && !isNaN(qty)) return sum + (cpr * qty);
        return sum;
      }, 0);
      let averageCostPerLoad = totalLoadsCreated > 0 ? totalSpent / totalLoadsCreated : null;
      let currentCostPerRound = null;
      
      // Get recipe cost data to check component availability
      try {
        const costResponse = await recipesAPI.getCost(recipe.id);
        const costData = costResponse.data;
        
        // Prefer backend-computed totals if provided
        if (typeof costData.total_spent === 'number') {
          totalSpent = Number(costData.total_spent || 0);
        }
        if (typeof costData.global_average_cost_per_round === 'number') {
          averageCostPerLoad = costData.global_average_cost_per_round;
        }
        if (costData.cost_available) {
          const c = (typeof costData.total_cost_per_round_2dp === 'number' ? costData.total_cost_per_round_2dp : costData.total_cost_per_round);
          if (typeof c === 'number') currentCostPerRound = Number(c || 0);
        }
        
        // Find components with insufficient quantity for 50 loads
        const insufficientComponents = [];
        for (const component of costData.component_breakdown) {
          let maxLoadsPossible;
          
          // Handle powder components (available_quantity is already in grains from API)
          if (component.type && component.type.toLowerCase() === 'powder') {
            // For powder, available_quantity is already in grains, quantity is in grains
            maxLoadsPossible = Math.floor(component.available_quantity / component.quantity);
          } else {
            // For other components, use simple division
            maxLoadsPossible = Math.floor(component.available_quantity / component.quantity);
          }
          
          if (maxLoadsPossible < 50) {
            insufficientComponents.push({
              name: component.component,
              type: component.type,
              available: component.available_quantity,
              needed: component.quantity,
              maxLoads: maxLoadsPossible
            });
          }
        }
        
        stats[recipe.id] = {
          totalLoadsCreated,
          totalLoadsRemaining,
          totalSpent,
          averageCostPerLoad: averageCostPerLoad ?? 0,
          currentCostPerRound,
          insufficientComponents,
          componentBreakdown: Array.isArray(costData.component_breakdown) ? costData.component_breakdown : [],
          costPerRound: costData?.cost_available ? Number(costData?.total_cost_per_round || 0) : null
        };
      } catch (error) {
        console.error(`Error getting cost data for recipe ${recipe.id}:`, error);
        stats[recipe.id] = {
          totalLoadsCreated,
          totalLoadsRemaining,
          totalSpent,
          averageCostPerLoad: averageCostPerLoad ?? 0,
          currentCostPerRound,
          insufficientComponents: [],
          componentBreakdown: [],
          costPerRound: null
        };
      }
    }
    
    setRecipeStats(stats);
  };

  const addCustomCartridge = async (cartridgeName) => {
    try {
      let name = String(cartridgeName || '').trim();
      if (name.startsWith('.')) name = name.slice(1).trim();
      const response = await cartridgesAPI.add({ cartridge: name });
      setCartridges(prev => [...prev, response.data]);
      setValue('cartridge', response.data.value);
      toast.success('Custom cartridge added successfully');
    } catch (error) {
      console.error('Error adding custom cartridge:', error);
      toast.error('Failed to add custom cartridge');
    }
  };

  const onSubmit = async (data) => {
    try {
      // Validate that all required components are selected
      if (!data.brass_id || !data.bullet_id || !data.primer_id || !data.powder_id) {
        toast.error('Please select one component of each type (brass, bullet, primer, powder)');
        return;
      }

      // Prepare components array
      const components = [
        { component_id: parseInt(data.brass_id), quantity: 1, unit: 'piece' },
        { component_id: parseInt(data.bullet_id), quantity: 1, unit: 'piece' },
        { component_id: parseInt(data.primer_id), quantity: 1, unit: 'piece' },
        { component_id: parseInt(data.powder_id), quantity: parseFloat(data.powder_charge), unit: 'grains' }
      ];

      // Normalize intended_firearms to always be an array
      let intended = data.intended_firearms;
      if (Array.isArray(intended)) {
        // ok
      } else if (intended != null && intended !== '') {
        intended = [String(intended)];
      } else {
        intended = [];
      }

      const recipeData = {
        name: data.name,
        description: data.description,
        notes: data.notes,
        components: components,
        cartridge: data.cartridge,
        cartridge_other: data.cartridge === 'other' ? data.cartridge_other : null,
        self_cannelured: data.self_cannelured || false,
        cannelure_depth: data.cannelure_depth ? parseFloat(data.cannelure_depth) : null,
        case_trim_length: data.case_trim_length ? parseFloat(data.case_trim_length) : null,
        cartridge_overall_length: data.cartridge_overall_length ? parseFloat(data.cartridge_overall_length) : null,
        seating_depth: data.seating_depth ? parseFloat(data.seating_depth) : null,
        crimp_type: data.crimp_type,
        crimp_strength: data.crimp_strength ? parseFloat(data.crimp_strength) : null,
        intended_firearms: intended.length > 0 ? JSON.stringify(intended) : null
      };

      if (editingRecipe) {
        await recipesAPI.update(editingRecipe.id, recipeData);
        toast.success('Recipe updated successfully');
        // Fetch all recipes to get complete data including populated components
        // The update endpoint only returns {id, message}, not full recipe data
        const recipesRes = await recipesAPI.getAll();
        setRecipes(Array.isArray(recipesRes.data) ? recipesRes.data : []);
        setEditingRecipe(null);
      } else {
        const res = await recipesAPI.create(recipeData);
        toast.success('Recipe created successfully');
        // Fetch all recipes to get complete data including populated components
        // This ensures filters work correctly with the new recipe
        const recipesRes = await recipesAPI.getAll();
        setRecipes(Array.isArray(recipesRes.data) ? recipesRes.data : []);
        // Initialize stats for new recipe
        const newRecipeId = res?.data?.id;
        if (newRecipeId) {
          setRecipeStats(prev => ({
            ...prev,
            [newRecipeId]: {
              totalLoadsCreated: 0,
              totalLoadsRemaining: 0,
              totalSpent: 0,
              averageCostPerLoad: null,
              currentCostPerRound: null,
              insufficientComponents: []
            }
          }));
        }
      }
      
      setShowForm(false);
      reset();
    } catch (error) {
      console.error('Error saving recipe:', error);
      const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
      const prefix = editingRecipe ? 'Failed to update recipe' : 'Failed to create recipe';
      toast.error(backendMsg ? `${prefix}: ${backendMsg}` : prefix);
    }
  };

  const handleEdit = (recipe) => {
    setEditingRecipe(recipe);
    setShowEditModal(true);
    
    // Pre-populate form with recipe data
    const formData = {
      name: recipe.name,
      description: recipe.description,
      notes: recipe.notes,
      cartridge: recipe.cartridge,
      cartridge_other: recipe.cartridge_other,
      self_cannelured: recipe.self_cannelured,
      cannelure_depth: recipe.cannelure_depth,
      case_trim_length: recipe.case_trim_length,
      cartridge_overall_length: recipe.cartridge_overall_length,
      seating_depth: recipe.seating_depth,
      crimp_type: recipe.crimp_type,
      crimp_strength: recipe.crimp_strength,
      intended_firearms: recipe.intended_firearms ? JSON.parse(recipe.intended_firearms) : []
    };
    
    // Set component selections
    recipe.components.forEach(comp => {
      if (comp.component_type === 'brass') formData.brass_id = comp.component.id.toString();
      if (comp.component_type === 'bullet') formData.bullet_id = comp.component.id.toString();
      if (comp.component_type === 'primer') formData.primer_id = comp.component.id.toString();
      if (comp.component_type === 'powder') {
        formData.powder_id = comp.component.id.toString();
        formData.powder_charge = comp.quantity.toString();
      }
    });
    
    // Guard against clearing dependent selects during prefill
    isPrefillingRef.current = true;
    reset(formData);
    try {
      const loadsExist = (loads || []).some(l => l.recipe?.id === recipe.id && (l.quantity_created || 0) > 0);
      setFieldsLocked(loadsExist);
    } catch { setFieldsLocked(false); }
    setTimeout(() => { isPrefillingRef.current = false; }, 0);
  };

  const handleCancel = () => {
    setShowForm(false);
    setShowEditModal(false);
    setEditingRecipe(null);
    setFieldsLocked(false);
    try { reset({}); } catch {}
  };

  const getComponentAvailability = async (recipeId) => {
    try {
      const response = await api.get(`/recipes/${recipeId}/cost`);
      const data = response.data;
      return data.component_breakdown || [];
    } catch (error) {
      console.error('Error fetching component availability:', error);
      return [];
    }
  };

  const handleRecipeClick = async (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeModal(true);
    setLoadingAvailability(true);
    
    try {
      const availability = await getComponentAvailability(recipe.id);
      setComponentAvailability(availability);
    } catch (error) {
      console.error('Error loading component availability:', error);
      setComponentAvailability([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const closeRecipeModal = () => {
    const closingId = selectedRecipe?.id;
    setShowRecipeModal(false);
    setSelectedRecipe(null);
    setComponentAvailability([]);
    setLoadingAvailability(false);
    // Ensure any form locks are cleared when leaving edit contexts
    setFieldsLocked(false);
    if (location.pathname === '/recipes') {
      const params = new URLSearchParams(location.search || '');
      if (closingId && params.get('recipeId') === String(closingId)) {
        params.delete('recipeId');
        const search = params.toString();
        const path = search ? `${location.pathname}?${search}` : location.pathname;
        navigate(path, { replace: true });
      }
    }
  };

  // Add Loads (from recipe) form hooks
  const { register: registerAddLoad, handleSubmit: handleSubmitAddLoad, reset: resetAddLoad, formState: { errors: errorsAddLoad } } = useForm();
  const { register: registerSession, handleSubmit: handleSubmitSession, reset: resetSession, control: controlSession, formState: { errors: errorsSession } } = useForm({ defaultValues: { velocities: [] } });
  const { fields: fieldsSession, append: appendSessionVelocity, remove: removeSessionVelocity } = useFieldArray({ control: controlSession, name: 'velocities' });

  const openAddLoadsFromRecipe = async () => {
    if (!selectedRecipe) return;
    try {
      const res = await loadsAPI.getCaseBatches(selectedRecipe.id);
      setRecipeCaseBatches(Array.isArray(res.data) ? res.data : []);
      setShowAddLoadsFromRecipe(true);
    } catch (e) {
      toast.error('Failed to load case batches');
    }
  };

  const onSubmitAddLoadsFromRecipe = async (data) => {
    if (!selectedRecipe) return;
    try {
      const res = await loadsAPI.create({
        recipe_id: selectedRecipe.id,
        case_batch_id: parseInt(data.case_batch_id),
        quantity_created: parseInt(data.quantity_created),
        date_created: data.date_created,
        notes: data.notes || ''
      });
      toast.success('Load batch created');
      setShowAddLoadsFromRecipe(false);
      resetAddLoad();
      
      // Incremental update: fetch only loads and update stats for this recipe
      const loadsRes = await loadsAPI.getAll();
      const newLoads = Array.isArray(loadsRes.data) ? loadsRes.data : [];
      setLoads(newLoads);
      
      // Update stats only for the affected recipe (faster than recalculating all)
      const recipeLoads = newLoads.filter(l => {
        const lrid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
        return String(lrid) === String(selectedRecipe.id);
      });
      const totalLoadsCreated = recipeLoads.reduce((sum, load) => sum + (Number(load.quantity_created) || 0), 0);
      const totalLoadsRemaining = recipeLoads.reduce((sum, load) => sum + (Number(load.remaining_quantity) || 0), 0);
      const totalSpent = recipeLoads.reduce((sum, load) => {
        const tc = Number(load.total_cost);
        if (!isNaN(tc) && tc > 0) return sum + tc;
        const cpr = Number(load.cost_per_round);
        const qty = Number(load.quantity_created);
        if (!isNaN(cpr) && !isNaN(qty)) return sum + (cpr * qty);
        return sum;
      }, 0);
      const averageCostPerLoad = totalLoadsCreated > 0 ? totalSpent / totalLoadsCreated : null;
      
      setRecipeStats(prev => ({
        ...prev,
        [selectedRecipe.id]: {
          ...prev[selectedRecipe.id],
          totalLoadsCreated,
          totalLoadsRemaining,
          totalSpent,
          averageCostPerLoad,
        }
      }));
    } catch (e) {
      const msg = e?.response?.data?.message || 'Failed to create load batch';
      toast.error(msg);
    }
  };

  

  const openSessionFromRecipe = () => {
    if (!selectedRecipe) return;
    resetSession({ velocities: [] });
    setShowSessionFromRecipe(true);
  };

  const onSubmitSessionFromRecipe = async (data) => {
    try {
      const res = await firingSessionsAPI.create({
        firearm_id: parseInt(data.firearm_id),
        load_id: parseInt(data.load_id),
        location: data.location,
        temperature: data.temperature ? parseFloat(data.temperature) : undefined,
        rounds_fired: parseInt(data.rounds_fired),
        session_date: data.session_date,
        notes: data.notes || '',
        velocities: (data.velocities || []).map(v => v && v.velocity ? parseFloat(v.velocity) : null).filter(v => v !== null)
      });
      toast.success('Firing session recorded');
      const loadForEvent = loads.find((l) => String(l.id) === String(data.load_id));
      const recipeIdForEvent = loadForEvent?.recipe?.id ?? selectedRecipe?.id ?? null;
      
      setShowSessionFromRecipe(false);
      resetSession();
      
      // Incremental update: fetch only loads and update stats for this recipe
      const loadsRes = await loadsAPI.getAll();
      const newLoads = Array.isArray(loadsRes.data) ? loadsRes.data : [];
      setLoads(newLoads);
      
      // Add new session to local state
      if (res?.data) {
        setSessions(prev => [...prev, res.data]);
      }
      
      // Update stats only for the affected recipe
      if (recipeIdForEvent) {
        const recipeLoads = newLoads.filter(l => {
          const lrid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
          return String(lrid) === String(recipeIdForEvent);
        });
        const totalLoadsCreated = recipeLoads.reduce((sum, load) => sum + (Number(load.quantity_created) || 0), 0);
        const totalLoadsRemaining = recipeLoads.reduce((sum, load) => sum + (Number(load.remaining_quantity) || 0), 0);
        const totalSpent = recipeLoads.reduce((sum, load) => {
          const tc = Number(load.total_cost);
          if (!isNaN(tc) && tc > 0) return sum + tc;
          const cpr = Number(load.cost_per_round);
          const qty = Number(load.quantity_created);
          if (!isNaN(cpr) && !isNaN(qty)) return sum + (cpr * qty);
          return sum;
        }, 0);
        const averageCostPerLoad = totalLoadsCreated > 0 ? totalSpent / totalLoadsCreated : null;
        
        setRecipeStats(prev => ({
          ...prev,
          [recipeIdForEvent]: {
            ...prev[recipeIdForEvent],
            totalLoadsCreated,
            totalLoadsRemaining,
            totalSpent,
            averageCostPerLoad,
          }
        }));
      }
      
      // Dispatch event for other pages (Loads, Firearms, etc.) - they will handle their own updates
      window.dispatchEvent(new CustomEvent('firingSessionChanged', {
        detail: { action: 'created', sessionId: res?.data?.id, loadId: data.load_id, recipeId: recipeIdForEvent }
      }));
    } catch (e) {
      toast.error('Failed to record session');
    }
  };

  const handleUploadRecipe = async (recipeId, files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    try {
      await uploadsAPI.upload('recipe', recipeId, arr);
      const res = await uploadsAPI.list('recipe', recipeId);
      setImagesByRecipe(prev => ({ ...prev, [recipeId]: res.data }));
      toast.success('Images uploaded');
    } catch {
      toast.error('Failed to upload images');
    }
  };

  const removeRecipeImage = async (recipeId, imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImagesByRecipe(prev => ({ ...prev, [recipeId]: (prev[recipeId] || []).filter(i => i.id !== imageId) }));
      toast.success('Image deleted');
    } catch {
      toast.error('Failed to delete image');
    }
  };

  const getRemainingLoadsForRecipe = (recipeId) => {
    const recipeLoads = (loads || []).filter(load => {
      const lrid = (load && load.recipe && load.recipe.id) || load?.recipe_id;
      return String(lrid) === String(recipeId);
    });
    return recipeLoads.reduce((total, load) => total + load.remaining_quantity, 0);
  };

  const getTotalLoadsCreated = (recipeId) => {
    const recipeLoads = (loads || []).filter(load => {
      const lrid = (load && load.recipe && load.recipe.id) || load?.recipe_id;
      return String(lrid) === String(recipeId);
    });
    return recipeLoads.reduce((total, load) => total + load.quantity_created, 0);
  };

  const getTotalSpent = (recipeId) => {
    const recipeLoads = (loads || []).filter(load => {
      const lrid = (load && load.recipe && load.recipe.id) || load?.recipe_id;
      return String(lrid) === String(recipeId);
    });
    return recipeLoads.reduce((total, load) => {
      const tc = Number(load.total_cost);
      if (!isNaN(tc) && tc > 0) return total + tc;
      const cpr = Number(load.cost_per_round);
      const qty = Number(load.quantity_created);
      if (!isNaN(cpr) && !isNaN(qty)) return total + (cpr * qty);
      return total;
    }, 0);
  };

  const getAverageCostPerLoad = (recipeId) => {
    const stats = recipeStats[recipeId];
    if (stats && typeof stats.averageCostPerLoad === 'number') return stats.averageCostPerLoad;
    // Fallback if stats not ready
    const recipeLoads = (loads || []).filter(load => {
      const lrid = (load && load.recipe && load.recipe.id) || load?.recipe_id;
      return String(lrid) === String(recipeId);
    });
    if (recipeLoads.length === 0) return null;
    const totalCost = recipeLoads.reduce((total, load) => {
      const tc = Number(load.total_cost);
      if (!isNaN(tc) && tc > 0) return total + tc;
      const cpr = Number(load.cost_per_round);
      const qty = Number(load.quantity_created);
      if (!isNaN(cpr) && !isNaN(qty)) return total + (cpr * qty);
      return total;
    }, 0);
    const totalLoadsCreated = recipeLoads.reduce((total, load) => total + (Number(load.quantity_created) || 0), 0);
    return totalLoadsCreated > 0 ? totalCost / totalLoadsCreated : null;
  };

  // Get the last activity date for a recipe (most recent load creation or firing session)
  const getLastActivityDate = (recipeId) => {
    // Build a map of loadId -> recipeId for session lookups
    const loadIdToRecipeId = new Map();
    (loads || []).forEach(l => {
      const rid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
      const ids = (Array.isArray(l.batch_ids) && l.batch_ids.length > 0) ? l.batch_ids : [l.id];
      ids.forEach(id => loadIdToRecipeId.set(id, rid));
    });
    
    // Get all load dates for this recipe
    const loadDates = (loads || [])
      .filter(load => {
        const lrid = (load && load.recipe && load.recipe.id) || load?.recipe_id;
        return String(lrid) === String(recipeId);
      })
      .map(load => load.date_created ? new Date(load.date_created).getTime() : 0)
      .filter(d => d > 0);
    
    // Get all session dates for this recipe
    const sessionDates = (sessions || [])
      .filter(session => {
        const loadId = (session && session.load && session.load.id) || session?.load_id;
        return String(loadIdToRecipeId.get(loadId)) === String(recipeId);
      })
      .map(session => session.session_date ? new Date(session.session_date).getTime() : 0)
      .filter(d => d > 0);
    
    const allDates = [...loadDates, ...sessionDates];
    return allDates.length > 0 ? Math.max(...allDates) : 0;
  };

  const getComponentByType = (recipe, componentType) => {
    return recipe.components?.find(comp => comp.component_type.toLowerCase() === componentType.toLowerCase());
  };

  const matchesFilter = (recipe, filterKey, filterValue) => {
    // filterValue is now an array; if empty, match all
    if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) return true;
    
    const values = filterValue.map(v => v.toLowerCase());
    
    switch (filterKey) {
      case 'cartridge':
        return values.includes(recipe.cartridge?.toLowerCase());
      
      case 'caseBrand':
        const brass = getComponentByType(recipe, 'brass');
        return values.includes(brass?.component.brand?.toLowerCase());
      
      case 'caseHeadstamp':
        const brassComp = getComponentByType(recipe, 'brass');
        return values.includes(brassComp?.component.head_stamp?.toLowerCase());
      
      case 'bulletBrand':
        const bullet = getComponentByType(recipe, 'bullet');
        return values.includes(bullet?.component.brand?.toLowerCase());
      
      case 'bulletModel':
        const bulletComp = getComponentByType(recipe, 'bullet');
        return values.includes(bulletComp?.component.model?.toLowerCase());
      
      case 'bulletWeight':
        const bulletWeight = getComponentByType(recipe, 'bullet');
        return values.includes(bulletWeight?.component.weight_grains?.toString());
      
      case 'bulletMaterial':
        const bulletMaterial = getComponentByType(recipe, 'bullet');
        return values.includes(bulletMaterial?.component.core_material?.toLowerCase());
      
      case 'bulletCovering':
        const bulletCovering = getComponentByType(recipe, 'bullet');
        return values.includes(bulletCovering?.component.covering?.toLowerCase());
      
      case 'bulletShape':
        const bulletShape = getComponentByType(recipe, 'bullet');
        return values.includes(bulletShape?.component.shape?.toLowerCase());
      
      case 'primerBrand':
        const primer = getComponentByType(recipe, 'primer');
        return values.includes(primer?.component.brand?.toLowerCase());
      
      case 'primerModel':
        const primerComp = getComponentByType(recipe, 'primer');
        return values.includes(primerComp?.component.model?.toLowerCase());
      
      case 'primerFirearmType':
        const primerFirearm = getComponentByType(recipe, 'primer');
        return values.includes(primerFirearm?.component.firearm_type?.toLowerCase());
      
      case 'primerSize':
        const primerSize = getComponentByType(recipe, 'primer');
        return values.includes(primerSize?.component.primer_size?.toLowerCase());
      
      case 'primerMagnum':
        const primerMagnum = getComponentByType(recipe, 'primer');
        return values.includes(String(primerMagnum?.component.magnum));
      
      case 'powderBrand':
        const powder = getComponentByType(recipe, 'powder');
        return values.includes(powder?.component.brand?.toLowerCase());
      
      case 'powderModel':
        const powderComp = getComponentByType(recipe, 'powder');
        return values.includes(powderComp?.component.model?.toLowerCase());
      
      case 'favorites':
        return values.includes(String(!!recipe.is_favorite));
      
      default:
        return true;
    }
  };

  const filterRecipes = (recipes) => {
    return recipes.filter(recipe => {
      return Object.entries(filters).every(([key, value]) => {
        return matchesFilter(recipe, key, value);
      });
    });
  };

  const sortRecipes = (recipes) => {
    return [...recipes].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy.field) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'favorites':
          // Sort favorites to top when asc (true > false), reverse when desc
          aValue = !!a.is_favorite ? 1 : 0;
          bValue = !!b.is_favorite ? 1 : 0;
          break;
        case 'cartridge':
          aValue = (a.cartridge || '').toLowerCase();
          bValue = (b.cartridge || '').toLowerCase();
          break;
        case 'costPerRound':
          aValue = getAverageCostPerLoad(a.id);
          bValue = getAverageCostPerLoad(b.id);
          break;
        case 'loadsRemaining':
          aValue = getRemainingLoadsForRecipe(a.id);
          bValue = getRemainingLoadsForRecipe(b.id);
          break;
        case 'totalCreated':
          aValue = getTotalLoadsCreated(a.id);
          bValue = getTotalLoadsCreated(b.id);
          break;
        case 'totalSpent':
          aValue = getTotalSpent(a.id);
          bValue = getTotalSpent(b.id);
          break;
        case 'dateCreated':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case 'lastActivity':
          aValue = getLastActivityDate(a.id);
          bValue = getLastActivityDate(b.id);
          break;
        default:
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
      }
      
      if (sortBy.direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  const filteredRecipes = useMemo(() => filterRecipes(recipes), [recipes, filters]);
  const sortedRecipes = useMemo(() => sortRecipes(filteredRecipes), [filteredRecipes, sortBy]);
  const getFilteredAndSortedRecipes = () => sortedRecipes;

  const buildGroupKey = (recipe) => {
    try {
      const brass = getComponentByType(recipe, 'brass');
      const bullet = getComponentByType(recipe, 'bullet');
      const primer = getComponentByType(recipe, 'primer');
      const powder = getComponentByType(recipe, 'powder');
      
      // Build primer abbreviation (e.g., SP, SR, LR, SPM, LRM)
      const primerSize = (primer?.component.primer_size || '').toLowerCase();
      const primerType = (primer?.component.firearm_type || '').toLowerCase();
      const isMagnum = primer?.component.magnum;
      const sizeAbbrev = primerSize.startsWith('small') ? 'S' : primerSize.startsWith('large') ? 'L' : primerSize.charAt(0).toUpperCase();
      const typeAbbrev = primerType.startsWith('pistol') ? 'P' : primerType.startsWith('rifle') ? 'R' : primerType.charAt(0).toUpperCase();
      const primerAbbrev = `${sizeAbbrev}${typeAbbrev}${isMagnum ? 'M' : ''}`;
      
      const parts = [
        (brass?.component.head_stamp || 'Unknown Headstamp').trim().toUpperCase(),
        `${bullet?.component.brand || 'Unknown'} ${bullet?.component.model || 'Bullet'}`.trim().toUpperCase(),
        `${bullet?.component.weight_grains || 0}gr`,
        (bullet?.component.core_material || 'core').toUpperCase(),
        (bullet?.component.covering || 'cover').toUpperCase(),
        (bullet?.component.shape || 'shape').toUpperCase(),
        `${primer?.component.brand || 'Primer'} ${primer?.component.model || ''}`.trim().toUpperCase(),
        primerAbbrev,
        `${powder?.component.brand || 'Powder'} ${powder?.component.model || ''}`.trim().toUpperCase()
      ];
      return parts.join(' | ');
    } catch {
      return 'UNCATEGORIZED';
    }
  };

  // Parse group key into colored segments for display
  const renderGroupKey = (key) => {
    if (key === 'UNCATEGORIZED') {
      return <span className="text-gray-600 font-bold">{key}</span>;
    }
    const parts = key.split(' | ');
    // parts[0] = brass (headstamp)
    // parts[1-5] = bullet (brand/model, weight, core, covering, shape)
    // parts[6-7] = primer (brand/model, abbreviation like SP/SPM/LR/LRM)
    // parts[8] = powder (brand/model)
    const brassText = parts[0];
    const bulletText = parts.slice(1, 6).join(' ');
    const primerText = parts.slice(6, 8).join(' ');
    const powderText = parts[8] || '';
    
    return (
      <>
        <span className="text-yellow-700 font-bold">{brassText}</span>
        <span className="text-gray-400 font-bold"> | </span>
        <span className="text-blue-700 font-bold">{bulletText}</span>
        <span className="text-gray-400 font-bold"> | </span>
        <span className="text-red-700 font-bold">{primerText}</span>
        <span className="text-gray-400 font-bold"> | </span>
        <span className="text-green-700 font-bold">{powderText}</span>
      </>
    );
  };

  const groupedRecipes = useMemo(() => {
    // Group consecutive recipes with the same key to preserve sort order
    // This may result in the same key appearing multiple times if recipes are interleaved by sort
    const groups = [];
    let currentKey = null;
    let currentItems = [];
    
    sortedRecipes.forEach(recipe => {
      const key = buildGroupKey(recipe);
      if (key !== currentKey) {
        // Save the previous group if it has items
        if (currentItems.length > 0) {
          groups.push({ key: currentKey, items: currentItems });
        }
        // Start a new group
        currentKey = key;
        currentItems = [recipe];
      } else {
        // Add to current group
        currentItems.push(recipe);
      }
    });
    
    // Don't forget the last group
    if (currentItems.length > 0) {
      groups.push({ key: currentKey, items: currentItems });
    }
    
    return groups;
  }, [sortedRecipes]);

  const toggleFavorite = async (e, recipe) => {
    e.stopPropagation();
    try {
      const next = !recipe.is_favorite;
      setRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, is_favorite: next } : r));
      await recipesAPI.toggleFavorite(recipe.id, next);
    } catch {
      setRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, is_favorite: recipe.is_favorite } : r));
    }
  };

  const handleFilterChange = (key, value) => {
    // value is now an array
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleFilterValue = (key, value) => {
    setFilters(prev => {
      const currentValues = prev[key] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [key]: newValues
      };
    });
  };

  const clearFilters = () => {
    setFilters({
      cartridge: [],
      caseBrand: [],
      caseHeadstamp: [],
      bulletBrand: [],
      bulletModel: [],
      bulletWeight: [],
      bulletMaterial: [],
      bulletCovering: [],
      bulletShape: [],
      primerBrand: [],
      primerModel: [],
      primerFirearmType: [],
      primerSize: [],
      primerMagnum: [],
      powderBrand: [],
      powderModel: [],
      favorites: []
    });
  };

  // Define filter categories for logical grouping
  const filterCategories = {
    case: ['caseBrand', 'caseHeadstamp'],
    bullet: ['bulletBrand', 'bulletModel', 'bulletWeight', 'bulletMaterial', 'bulletCovering', 'bulletShape'],
    primer: ['primerBrand', 'primerModel', 'primerFirearmType', 'primerSize', 'primerMagnum'],
    powder: ['powderBrand', 'powderModel'],
    other: ['cartridge', 'favorites']
  };

  // Get the category for a given field
  const getFieldCategory = (field) => {
    for (const [category, fields] of Object.entries(filterCategories)) {
      if (fields.includes(field)) return category;
    }
    return 'other';
  };

  // Check if a recipe matches filters within a category (excluding the current field)
  const recipeMatchesCategoryFilters = (recipe, category, excludeField) => {
    const categoryFields = filterCategories[category] || [];
    return categoryFields.every(filterField => {
      if (filterField === excludeField) return true; // Skip the field we're getting options for
      const filterValue = filters[filterField] || [];
      if (filterValue.length === 0) return true; // No filter applied
      
      let recipeValue;
      switch (filterField) {
        case 'caseBrand':
          recipeValue = getComponentByType(recipe, 'brass')?.component.brand?.toLowerCase();
          break;
        case 'caseHeadstamp':
          recipeValue = getComponentByType(recipe, 'brass')?.component.head_stamp?.toLowerCase();
          break;
        case 'bulletBrand':
          recipeValue = getComponentByType(recipe, 'bullet')?.component.brand?.toLowerCase();
          break;
        case 'bulletModel':
          recipeValue = getComponentByType(recipe, 'bullet')?.component.model?.toLowerCase();
          break;
        case 'bulletWeight':
          recipeValue = getComponentByType(recipe, 'bullet')?.component.weight_grains?.toString();
          break;
        case 'bulletMaterial':
          recipeValue = getComponentByType(recipe, 'bullet')?.component.core_material?.toLowerCase();
          break;
        case 'bulletCovering':
          recipeValue = getComponentByType(recipe, 'bullet')?.component.covering?.toLowerCase();
          break;
        case 'bulletShape':
          recipeValue = getComponentByType(recipe, 'bullet')?.component.shape?.toLowerCase();
          break;
        case 'primerBrand':
          recipeValue = getComponentByType(recipe, 'primer')?.component.brand?.toLowerCase();
          break;
        case 'primerModel':
          recipeValue = getComponentByType(recipe, 'primer')?.component.model?.toLowerCase();
          break;
        case 'primerFirearmType':
          recipeValue = getComponentByType(recipe, 'primer')?.component.firearm_type?.toLowerCase();
          break;
        case 'primerSize':
          recipeValue = getComponentByType(recipe, 'primer')?.component.primer_size?.toLowerCase();
          break;
        case 'primerMagnum':
          recipeValue = String(getComponentByType(recipe, 'primer')?.component.magnum);
          break;
        case 'powderBrand':
          recipeValue = getComponentByType(recipe, 'powder')?.component.brand?.toLowerCase();
          break;
        case 'powderModel':
          recipeValue = getComponentByType(recipe, 'powder')?.component.model?.toLowerCase();
          break;
        default:
          return true;
      }
      
      return filterValue.map(v => v.toLowerCase()).includes(recipeValue);
    });
  };

  // Get unique values for a filter field from recipes, filtered by other selections in the same category
  const getUniqueFilterValues = (field) => {
    const values = new Set();
    const category = getFieldCategory(field);
    
    recipes.forEach(recipe => {
      // Check if this recipe matches other filters in the same category
      if (!recipeMatchesCategoryFilters(recipe, category, field)) {
        return; // Skip this recipe
      }
      
      let val;
      switch (field) {
        case 'cartridge':
          val = recipe.cartridge;
          break;
        case 'caseBrand':
          val = getComponentByType(recipe, 'brass')?.component.brand;
          break;
        case 'caseHeadstamp':
          val = getComponentByType(recipe, 'brass')?.component.head_stamp;
          break;
        case 'bulletBrand':
          val = getComponentByType(recipe, 'bullet')?.component.brand;
          break;
        case 'bulletModel':
          val = getComponentByType(recipe, 'bullet')?.component.model;
          break;
        case 'bulletWeight':
          val = getComponentByType(recipe, 'bullet')?.component.weight_grains?.toString();
          break;
        case 'bulletMaterial':
          val = getComponentByType(recipe, 'bullet')?.component.core_material;
          break;
        case 'bulletCovering':
          val = getComponentByType(recipe, 'bullet')?.component.covering;
          break;
        case 'bulletShape':
          val = getComponentByType(recipe, 'bullet')?.component.shape;
          break;
        case 'primerBrand':
          val = getComponentByType(recipe, 'primer')?.component.brand;
          break;
        case 'primerModel':
          val = getComponentByType(recipe, 'primer')?.component.model;
          break;
        case 'primerFirearmType':
          val = getComponentByType(recipe, 'primer')?.component.firearm_type;
          break;
        case 'primerSize':
          val = getComponentByType(recipe, 'primer')?.component.primer_size;
          break;
        case 'primerMagnum':
          val = String(getComponentByType(recipe, 'primer')?.component.magnum);
          break;
        case 'powderBrand':
          val = getComponentByType(recipe, 'powder')?.component.brand;
          break;
        case 'powderModel':
          val = getComponentByType(recipe, 'powder')?.component.model;
          break;
        case 'favorites':
          val = String(!!recipe.is_favorite);
          break;
        default:
          val = null;
      }
      if (val && val.toString().trim() && val !== 'undefined') {
        values.add(val.toString().trim());
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
  };

  // Color variants for filter dropdowns based on component type
  const filterColorVariants = {
    brass: {
      border: 'border-yellow-500',
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      label: 'text-yellow-800',
      checkbox: 'text-yellow-600',
      hover: 'hover:bg-yellow-50'
    },
    bullet: {
      border: 'border-blue-500',
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      label: 'text-blue-800',
      checkbox: 'text-blue-600',
      hover: 'hover:bg-blue-50'
    },
    primer: {
      border: 'border-red-500',
      bg: 'bg-red-50',
      text: 'text-red-700',
      label: 'text-red-800',
      checkbox: 'text-red-600',
      hover: 'hover:bg-red-50'
    },
    powder: {
      border: 'border-green-500',
      bg: 'bg-green-50',
      text: 'text-green-700',
      label: 'text-green-800',
      checkbox: 'text-green-600',
      hover: 'hover:bg-green-50'
    },
    default: {
      border: 'border-gray-500',
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      label: 'text-gray-800',
      checkbox: 'text-gray-600',
      hover: 'hover:bg-gray-100'
    }
  };

  // Dropdown checkbox filter component for recipes
  const RecipeFilterDropdown = ({ field, label, options, displayMap, colorVariant = 'default' }) => {
    const isOpen = openFilterDropdown === field;
    const selectedValues = filters[field] || [];
    const dropdownRef = useRef(null);
    const colors = filterColorVariants[colorVariant] || filterColorVariants.default;

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          if (openFilterDropdown === field) {
            setOpenFilterDropdown(null);
          }
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [field]);

    if (options.length === 0) return null;

    const handleToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Prevent focus-related scrolling
      if (e.currentTarget) {
        e.currentTarget.blur();
      }
      setOpenFilterDropdown(isOpen ? null : field);
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <label className={`block text-sm font-medium mb-1 ${colors.label}`}>{label}</label>
        <button
          type="button"
          tabIndex={-1}
          className={`w-full px-3 py-2 border rounded-md text-sm text-left flex items-center justify-between ${selectedValues.length > 0 ? `${colors.border} ${colors.bg}` : 'border-gray-300'}`}
          onMouseDown={handleToggle}
          onClick={(e) => e.preventDefault()}
        >
          <span className={selectedValues.length > 0 ? colors.text : 'text-gray-500'}>
            {selectedValues.length > 0 ? `${selectedValues.length} selected` : 'Any'}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {options.map(option => {
              const displayValue = displayMap ? displayMap[option] : option;
              return (
                <label
                  key={option}
                  className={`flex items-center px-3 py-2 ${colors.hover} cursor-pointer text-sm`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFilterValue(field, option);
                  }}
                >
                  <input
                    type="checkbox"
                    tabIndex={-1}
                    checked={selectedValues.includes(option)}
                    onChange={() => {}}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className={`mr-2 h-4 w-4 ${colors.checkbox} rounded border-gray-300 pointer-events-none`}
                  />
                  <span className="truncate">{displayValue}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => Array.isArray(value) && value.length > 0).length;
  };

  const handleSortChange = (field) => {
    setSortBy(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSelectedBullet = () => {
    if (!selectedBullet) return null;
    return componentsByType.bullet?.find(b => b.id === parseInt(selectedBullet));
  };

  const getCompatibleFirearmsForRecipe = (recipe) => {
    if (!recipe) return firearms;
    const caliber = recipe.cartridge || recipe.cartridge_other || '';
    if (!caliber) return firearms;
    return firearms.filter((f) => {
      let intended = [];
      if (f.intended_calibers) {
        try { intended = JSON.parse(f.intended_calibers) || []; } catch {}
      }
      return f.caliber === caliber || intended.includes(caliber);
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Recipes</h1>
        <div className="flex items-center space-x-2">
        <button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${getActiveFilterCount() > 0 ? 'btn-primary' : 'btn-secondary'}`}
          >
            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
            </svg>
            Filters
            {getActiveFilterCount() > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-white text-blue-600 rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </button>
          <button
            onClick={() => { try { reset({ crimp_strength: 1 }); } catch {}; setFieldsLocked(false); setShowForm(true); }}
          className="btn btn-primary"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Recipe
        </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Recipes</h3>
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear All Filters
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Favorites Filter */}
            <RecipeFilterDropdown
              field="favorites"
              label="Favorites"
              options={getUniqueFilterValues('favorites')}
              displayMap={{ 'true': 'Favorites only', 'false': 'Not favorites' }}
              colorVariant="default"
            />

            {/* Cartridge Filter */}
            <RecipeFilterDropdown
              field="cartridge"
              label="Cartridge"
              options={getUniqueFilterValues('cartridge')}
              colorVariant="default"
            />

            {/* Case Brand Filter */}
            <RecipeFilterDropdown
              field="caseBrand"
              label="Case Brand"
              options={getUniqueFilterValues('caseBrand')}
              colorVariant="brass"
            />

            {/* Case Headstamp Filter */}
            <RecipeFilterDropdown
              field="caseHeadstamp"
              label="Case Headstamp"
              options={getUniqueFilterValues('caseHeadstamp')}
              colorVariant="brass"
            />

            {/* Bullet Brand Filter */}
            <RecipeFilterDropdown
              field="bulletBrand"
              label="Bullet Brand"
              options={getUniqueFilterValues('bulletBrand')}
              colorVariant="bullet"
            />

            {/* Bullet Model Filter */}
            <RecipeFilterDropdown
              field="bulletModel"
              label="Bullet Model"
              options={getUniqueFilterValues('bulletModel')}
              colorVariant="bullet"
            />

            {/* Bullet Weight Filter */}
            <RecipeFilterDropdown
              field="bulletWeight"
              label="Bullet Weight (gr)"
              options={getUniqueFilterValues('bulletWeight')}
              colorVariant="bullet"
            />

            {/* Bullet Material Filter */}
            <RecipeFilterDropdown
              field="bulletMaterial"
              label="Bullet Material"
              options={getUniqueFilterValues('bulletMaterial')}
              colorVariant="bullet"
            />

            {/* Bullet Covering Filter */}
            <RecipeFilterDropdown
              field="bulletCovering"
              label="Bullet Covering"
              options={getUniqueFilterValues('bulletCovering')}
              colorVariant="bullet"
            />

            {/* Bullet Shape Filter */}
            <RecipeFilterDropdown
              field="bulletShape"
              label="Bullet Shape"
              options={getUniqueFilterValues('bulletShape')}
              colorVariant="bullet"
            />

            {/* Primer Brand Filter */}
            <RecipeFilterDropdown
              field="primerBrand"
              label="Primer Brand"
              options={getUniqueFilterValues('primerBrand')}
              colorVariant="primer"
            />

            {/* Primer Model Filter */}
            <RecipeFilterDropdown
              field="primerModel"
              label="Primer Model"
              options={getUniqueFilterValues('primerModel')}
              colorVariant="primer"
            />

            {/* Primer Firearm Type Filter */}
            <RecipeFilterDropdown
              field="primerFirearmType"
              label="Primer Firearm Type"
              options={getUniqueFilterValues('primerFirearmType')}
              colorVariant="primer"
            />

            {/* Primer Size Filter */}
            <RecipeFilterDropdown
              field="primerSize"
              label="Primer Size"
              options={getUniqueFilterValues('primerSize')}
              colorVariant="primer"
            />

            {/* Primer Magnum Filter */}
            <RecipeFilterDropdown
              field="primerMagnum"
              label="Primer Magnum"
              options={getUniqueFilterValues('primerMagnum')}
              displayMap={{ 'true': 'Magnum', 'false': 'Non-Magnum' }}
              colorVariant="primer"
            />

            {/* Powder Brand Filter */}
            <RecipeFilterDropdown
              field="powderBrand"
              label="Powder Brand"
              options={getUniqueFilterValues('powderBrand')}
              colorVariant="powder"
            />

            {/* Powder Model Filter */}
            <RecipeFilterDropdown
              field="powderModel"
              label="Powder Model"
              options={getUniqueFilterValues('powderModel')}
              colorVariant="powder"
            />
          </div>
        </div>
      )}

      {showPurchaseModal && purchaseComponent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" onClick={()=>setShowPurchaseModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Record Purchase — {purchaseComponent.brand} {purchaseComponent.model || purchaseComponent.caliber}</h3>
              <button onClick={()=>setShowPurchaseModal(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Quantity</label>
                <input type="number" className="input" value={purchaseForm.quantity} onChange={(e)=>handlePurchaseCalc('quantity', e.target.value)} />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input type="date" className="input" value={purchaseForm.purchase_date} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_date:e.target.value}))} />
              </div>
              <div>
                <label className="label">Total Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.total_cost} onChange={(e)=>handlePurchaseCalc('total_cost', e.target.value)} />
              </div>
              <div>
                <label className="label">Unit Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.unit_cost} onChange={(e)=>handlePurchaseCalc('unit_cost', e.target.value)} />
              </div>
              {(purchaseComponent?.component_type?.name || '').toLowerCase() === 'brass' && (
                <div className="md:col-span-2">
                  <label className="label">Starting Firing Count (brass)</label>
                  <input type="number" min="0" className="input" value={purchaseForm.starting_firing_count} onChange={(e)=>setPurchaseForm(prev=>({...prev, starting_firing_count:e.target.value}))} />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="label">Purchase Location</label>
                <input type="text" className="input" value={purchaseForm.purchase_location} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_location:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Website</label>
                <input type="url" className="input" value={purchaseForm.website} onChange={(e)=>setPurchaseForm(prev=>({...prev, website:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" value={purchaseForm.notes} onChange={(e)=>setPurchaseForm(prev=>({...prev, notes:e.target.value}))} />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={()=>setShowPurchaseModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitPurchaseFromRecipe}>Save Purchase</button>
            </div>
          </div>
        </div>
      )}

      {/* Sort Controls */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-blue-900">Sort Recipes</h3>
            <p className="text-sm text-blue-700">
              Showing {sortedRecipes.length} of {recipes.length} recipes
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-blue-700">Sort by:</span>
            <select
              value={sortBy.field}
              onChange={(e) => handleSortChange(e.target.value)}
              className="px-3 py-1 border border-blue-300 rounded text-sm"
            >
              <option value="name">Name</option>
              <option value="cartridge">Cartridge</option>
              <option value="costPerRound">Cost per Round</option>
              <option value="loadsRemaining">Loads Remaining</option>
              <option value="totalCreated">Total Created</option>
              <option value="totalSpent">Total Spent</option>
              <option value="favorites">Favorites</option>
              <option value="dateCreated">Date Created</option>
              <option value="lastActivity">Last Activity</option>
            </select>
            <button
              onClick={() => handleSortChange(sortBy.field)}
              className="px-2 py-1 text-blue-600 hover:text-blue-800"
            >
              {sortBy.direction === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Recipe Create Form (top-of-page) */}
      {showForm && !editingRecipe && (
        <div ref={recipeFormRef} className="card p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Create New Recipe</h2>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Recipe Name</label>
                <input
                  type="text"
                  {...register('name', { required: 'Recipe name is required' })}
                  className="input"
                  placeholder="e.g., .357 Magnum Target Load"
                  onChange={(e) => {
                    // If user types something different than our last auto, stop auto mode
                    try {
                      const v = e.target.value || '';
                      if (v && v !== lastAutoNameRef.current) {
                        setIsNameAuto(false);
                      }
                    } catch {}
                  }}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="label">Cartridge</label>
                <div>
                <select
                  {...register('cartridge', { required: 'Cartridge is required' })}
                    className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    disabled={fieldsLocked}
                >
                  <option value="">Select cartridge...</option>
                  {cartridges.map(cartridge => (
                    <option key={cartridge.id} value={cartridge.value}>
                      {cartridge.value}
                    </option>
                  ))}
                  <option value="other">Other</option>
                </select>
                </div>
                {errors.cartridge && (
                  <p className="text-red-500 text-sm mt-1">{errors.cartridge.message}</p>
                )}
              </div>
            </div>

            {/* Custom Cartridge */}
            {cartridge === 'other' && (
              <div>
                <label className="label">Custom Cartridge</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    {...register('cartridge_other', { required: 'Custom cartridge name is required' })}
                    className="input flex-1"
                    placeholder="Enter cartridge name..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const customCartridge = watch('cartridge_other');
                      if (customCartridge) {
                        addCustomCartridge(customCartridge);
                      }
                    }}
                    className="btn btn-secondary"
                  >
                    Add
                  </button>
                </div>
                {errors.cartridge_other && (
                  <p className="text-red-500 text-sm mt-1">{errors.cartridge_other.message}</p>
                )}
              </div>
            )}

            {/* Component Selection (cartridge must be selected first) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Brass Selection */}
              <div>
                <label className="label">Brass</label>
                <select
                  {...register('brass_id', { required: 'Brass is required' })}
                  className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={!effectiveCartridge || fieldsLocked}
                >
                  <option value="">Select brass...</option>
                  {componentsByType.brass
                    ?.filter(brass => effectiveCartridge && (brass.caliber || '').toLowerCase() === effectiveCartridge.toLowerCase())
                    .map(brass => (
                    <option key={brass.id} value={brass.id}>
                      {brass.head_stamp || brass.model || brass.brand}
                    </option>
                  ))}
                </select>
                {errors.brass_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.brass_id.message}</p>
                )}
              </div>

              {/* Bullet Selection */}
              <div>
                <label className="label">Bullet</label>
                <select
                  {...register('bullet_id', { required: 'Bullet is required' })}
                  className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={!effectiveCartridge || fieldsLocked}
                >
                  <option value="">Select bullet...</option>
                  {componentsByType.bullet
                    ?.filter(bullet => {
                      try {
                        const arr = bullet.intended_cartridges ? JSON.parse(bullet.intended_cartridges) : [];
                        return effectiveCartridge && arr.map(v => (v || '').toString().toLowerCase()).includes(effectiveCartridge.toLowerCase());
                      } catch { return false; }
                    })
                    .map(bullet => (
                    <option key={bullet.id} value={bullet.id}>
                      {bullet.brand} {bullet.model} ({bullet.weight_grains}gr)
                    </option>
                  ))}
                </select>
                {errors.bullet_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.bullet_id.message}</p>
                )}
              </div>

              {/* Primer Selection */}
              <div>
                <label className="label">Primer</label>
                <select
                  {...register('primer_id', { required: 'Primer is required' })}
                  className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={!selectedBrass || fieldsLocked}
                >
                  <option value="">Select primer...</option>
                  {(() => {
                    if (!selectedBrass) return null;
                    const brass = componentsByType.brass?.find(b => b.id === parseInt(selectedBrass));
                    if (!brass) return null;
                    const size = (brass.primer_pocket_size || '').toLowerCase();
                    const type = (brass.primer_pocket_type || '').toLowerCase();
                    return componentsByType.primer
                      ?.filter(primer => {
                        const pSize = (primer.primer_size || '').toLowerCase();
                        const pType = (primer.firearm_type || '').toLowerCase();
                        if (!size) return false;
                        if (size === 'small') {
                          return pSize === 'small';
                        }
                        // large pockets: require matching firearm type
                        return pSize === 'large' && (!!type ? pType === type : true);
                      })
                      .map(primer => (
                    <option key={primer.id} value={primer.id}>
                      {primer.brand} {primer.model}
                    </option>
                      ));
                  })()}
                </select>
                {errors.primer_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.primer_id.message}</p>
                )}
              </div>

              {/* Powder Selection */}
              <div>
                <label className="label">Powder</label>
                <select
                  {...register('powder_id', { required: 'Powder is required' })}
                  className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={fieldsLocked}
                >
                  <option value="">Select powder...</option>
                  {componentsByType.powder?.map(powder => (
                    <option key={powder.id} value={powder.id}>
                      {powder.brand} {powder.model}
                    </option>
                  ))}
                </select>
                {errors.powder_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.powder_id.message}</p>
                )}
              </div>
            </div>

            {/* Powder Charge */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="label">Powder Charge (grains)</label>
                <input
                  type="number"
                  step="0.1"
                  {...register('powder_charge', { 
                    required: 'Powder charge is required',
                    min: { value: 0, message: 'Charge must be positive' },
                    max: { value: 999.9, message: 'Charge must be less than 1000' }
                  })}
                  className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  disabled={fieldsLocked}
                  placeholder="e.g., 5.5"
                />
                {errors.powder_charge && (
                  <p className="text-red-500 text-sm mt-1">{errors.powder_charge.message}</p>
                )}
              </div>
            </div>

            {/* Self-Cannelured Checkbox */}
            {selectedBullet && !getSelectedBullet()?.cannelured && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('self_cannelured')}
                  className="checkbox"
                />
                <label className="label">Self-Cannelured</label>
              </div>
            )}

            {/* Cannelure Depth */}
            {(getSelectedBullet()?.cannelured || selfCannelured) && (
              <div>
                <label className="label">Cannelure Depth (inches)</label>
                <input
                  type="number"
                  step="0.001"
                  {...register('cannelure_depth')}
                  className="input"
                  placeholder="e.g., 0.010"
                  readOnly={getSelectedBullet()?.cannelured && getSelectedBullet()?.cannelure_depth}
                  title={getSelectedBullet()?.cannelured && getSelectedBullet()?.cannelure_depth ? "Auto-populated from bullet data" : ""}
                />
                {getSelectedBullet()?.cannelured && getSelectedBullet()?.cannelure_depth && (
                  <p className="text-sm text-gray-600 mt-1">Auto-populated from bullet data</p>
                )}
              </div>
            )}

            {/* Dimension Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {selectedBullet && (
                <div className="col-span-full">
                  <p className="text-sm text-blue-600 mb-2">
                    <Calculator className="inline h-4 w-4 mr-1" />
                    Enter any two dimensions and the third will be calculated automatically using the selected bullet's length.
                  </p>
                </div>
              )}
              <div>
                <label className="label">Case Trim Length (inches)</label>
                <input
                  type="number"
                  step="0.001"
                  {...register('case_trim_length')}
                  className="input"
                  placeholder="e.g., 1.280"
                />
              </div>

              <div>
                <label className="label">Cartridge Overall Length (inches)</label>
                <input
                  type="number"
                  step="0.001"
                  {...register('cartridge_overall_length')}
                  className="input"
                  placeholder="e.g., 1.590"
                />
              </div>

              <div>
                <label className="label">Seating Depth (inches)</label>
                <input
                  type="number"
                  step="0.001"
                  {...register('seating_depth')}
                  className="input"
                  placeholder="e.g., 0.200"
                />
              </div>
            </div>

            {/* Crimp Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Crimp Type</label>
                <select {...register('crimp_type')} className="input">
                  <option value="">Select crimp type...</option>
                  <option value="Roll">Roll</option>
                  <option value="Taper">Taper</option>
                  <option value="Collet">Collet</option>
                </select>
              </div>

              <div>
                <label className="label">Crimp Strength (Die Turns)</label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  {...register('crimp_strength')}
                  className="w-full"
                />
                <div className="text-sm text-gray-600 mt-1">
                  Value: {watch('crimp_strength') || '0'}
                </div>
              </div>
            </div>

            {/* Intended Firearms */}
            <div>
              <label className="label">Intended Firearms</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                {firearms.map(firearm => (
                  <label key={firearm.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      value={firearm.id}
                      {...register('intended_firearms')}
                      className="checkbox"
                    />
                    <span className="text-sm">
                      {firearm.manufacturer} {firearm.model}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Description and Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Description</label>
                <textarea
                  {...register('description')}
                  className="input"
                  rows="3"
                  placeholder="Brief description of this recipe..."
                />
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea
                  {...register('notes')}
                  className="input"
                  rows="3"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            {/* Form Buttons */}
            <div className="flex space-x-3">
              <button type="submit" className="btn btn-primary">
                Create Recipe
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const next = {};
              groupedRecipes.forEach((group, idx) => {
                next[`${group.key}-${idx}`] = false;
              });
              setCollapsedGroups(next);
            }}
          >
            Expand All
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const next = {};
              groupedRecipes.forEach((group, idx) => {
                next[`${group.key}-${idx}`] = true;
              });
              setCollapsedGroups(next);
            }}
          >
            Collapse All
          </button>
        </div>
        <div className="text-sm text-gray-500">
          {sortedRecipes.length} recipe{sortedRecipes.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Recipes List */}
      <div className="space-y-4">
        {groupedRecipes.map(({ key, items }, groupIndex) => {
          const groupStateKey = `${key}-${groupIndex}`;
          const collapsed = collapsedGroups[groupStateKey] ?? true;
          return (
            <div key={groupStateKey} className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <button
                type="button"
                className={`w-full flex items-center justify-between px-4 py-3 text-left bg-white border-b border-gray-200 ${!collapsed ? 'sticky top-0 z-10 shadow-sm' : ''}`}
                onClick={() => setCollapsedGroups(prev => ({ ...prev, [groupStateKey]: !collapsed }))}
              >
                <div className="flex-1 min-w-0 mr-2">
                  <p className="text-sm">{renderGroupKey(key)}</p>
                  <p className="text-xs text-gray-500 mt-1">{items.length} recipe{items.length === 1 ? '' : 's'}</p>
                </div>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${collapsed ? '' : 'rotate-180'}`} />
              </button>

              {!collapsed && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map(recipe => {
          const stats = recipeStats[recipe.id] || {
            totalLoadsCreated: 0,
            totalLoadsRemaining: 0,
            totalSpent: 0,
            averageCostPerLoad: 0,
            insufficientComponents: []
          };
          
          const isDeleted = !!recipe.is_deleted;
          return (
            <div key={recipe.id} className={`card p-6 relative ${isDeleted ? 'opacity-70' : 'cursor-pointer hover:shadow-lg transition-shadow'}`} onClick={() => !isDeleted && handleRecipeClick(recipe)}>
            <div className="mb-2">
              <h3 className="font-semibold text-gray-900 text-lg">
                {isDeleted ? `Deleted — ${recipe.name}` : recipe.name}
              </h3>
              </div>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-3 gap-3">
              <span>{recipe.cartridge || recipe.cartridge_other || 'N/A'}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                  {!isDeleted && (
              <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(recipe); }}
                className="btn btn-secondary btn-sm"
                      title="Edit Recipe"
              >
                <Edit className="h-4 w-4" />
              </button>
                  )}
                  {!isDeleted && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintRecipe(recipe);
                      }}
                      className="btn btn-secondary btn-sm"
                      title="Print QR"
                    >
                      <Printer className="h-4 w-4" />
                    </button>
                  )}
                  {!isDeleted && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const formData = {
                          name: recipe.name,
                          description: recipe.description || '',
                          notes: recipe.notes || '',
                          cartridge: recipe.cartridge || '',
                          cartridge_other: recipe.cartridge_other || '',
                          self_cannelured: !!recipe.self_cannelured,
                          cannelure_depth: recipe.cannelure_depth ?? '',
                          case_trim_length: recipe.case_trim_length ?? '',
                          cartridge_overall_length: recipe.cartridge_overall_length ?? '',
                          seating_depth: recipe.seating_depth ?? '',
                          crimp_type: recipe.crimp_type || '',
                          crimp_strength: recipe.crimp_strength ?? '',
                          intended_firearms: (() => { try { return recipe.intended_firearms ? JSON.parse(recipe.intended_firearms) : []; } catch { return []; } })()
                        };
                        (recipe.components || []).forEach(comp => {
                          if (comp.component_type === 'brass') formData.brass_id = comp.component.id.toString();
                          if (comp.component_type === 'bullet') formData.bullet_id = comp.component.id.toString();
                          if (comp.component_type === 'primer') formData.primer_id = comp.component.id.toString();
                          if (comp.component_type === 'powder') { formData.powder_id = comp.component.id.toString(); formData.powder_charge = (comp.quantity ?? '').toString(); }
                        });
                        // Guard dependent-clearing effects during prefill
                        isPrefillingRef.current = true;
                        try { reset(formData); } catch {}
                        // Keep auto-name behavior intact
                        setTimeout(() => { isPrefillingRef.current = false; }, 0);
                        setFieldsLocked(false);
                        setEditingRecipe(null);
                        setShowEditModal(false);
                        setShowForm(true);
                        // Scroll to the form after it renders
                        setTimeout(() => {
                          recipeFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                      }}
                      className="btn btn-secondary btn-sm"
                      title="Copy Recipe"
                    >
                      <CopyIcon className="h-4 w-4" />
                    </button>
                  )}
                  {!isDeleted && canDeleteRecipe(recipe.id) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecipe(recipe);
                      }}
                      className="btn btn-danger btn-sm"
                      title="Delete Recipe"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
            {/* Thumbnails */}
            {Array.isArray(imagesByRecipe[recipe.id]) && imagesByRecipe[recipe.id].length > 0 && (
              <div className="mt-2">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {imagesByRecipe[recipe.id].map(file => {
                    const src = file.download_url || file.url;
                    const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                    const thumb = isImg ? src : (file.thumbnail_url || src);
                    const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                    return (
                      <div key={file.id} className="flex-shrink-0" onClick={(e) => { e.stopPropagation(); setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url }); }}>
                        {isPdf ? (
                          <PdfThumbnail
                            fileId={file.id}
                            url={src}
                            thumbnailUrl={file.thumbnail_url}
                            filename={file.filename}
                            sizeClass="w-16 h-16"
                          />
                        ) : (
                          <img src={thumb} alt={file.filename} className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
              {/* Recipe Statistics */}
              <div className="mt-4 space-y-3">
                {/* Cost and Load Statistics */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">{stats.currentCostPerRound != null ? `$${Number(stats.currentCostPerRound).toFixed(2)}` : 'NA'}</p>
                      <p className="text-xs text-gray-500">Current cost/load</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-900">{stats.totalLoadsCreated.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Total created</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Package className="h-4 w-4 text-orange-600" />
                    <div>
                      <p className="font-medium text-gray-900">{stats.totalLoadsRemaining.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Remaining</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-purple-600" />
                    <div>
                      <p className="font-medium text-gray-900">{(() => {
                        const avg = (recipeStats[recipe.id]?.averageCostPerLoad);
                        if (avg == null) return 'NA';
                        return `$${Number(avg).toFixed(2)}`;
                      })()}</p>
                      <p className="text-xs text-gray-500">Average cost/load</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-red-600" />
                    <div>
                      <p className="font-medium text-gray-900">${stats.totalSpent.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Total spent</p>
                    </div>
                  </div>
                </div>
                
                {/* Velocity by Firearm (if any sessions exist) */}
                {(() => {
                  const per = getVelocityByFirearmForRecipe(recipe.id) || [];
                  if (per.length === 0) return null;
                  return (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-700 mb-2">Velocity by Firearm</div>
                      <div className="space-y-1 text-xs">
                        {per.map((r, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-gray-700 truncate pr-2">{r.label}</span>
                            <span className="text-gray-900">Avg {r.average} • ES {r.es ?? '-'} • SD {r.sd ?? '-'} • N {r.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {/* Insufficient Components Warning */}
                {Array.isArray(stats.insufficientComponents) && stats.insufficientComponents.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-600" />
                      <p className="text-sm font-medium text-yellow-800">Low Component Stock</p>
                    </div>
                    <div className="space-y-1">
                      {stats.insufficientComponents.map((component, index) => (
                        <div key={index} className="text-xs text-gray-600">
                          <span className="font-medium">{component.name}</span>: 
                          Only {component.maxLoads} loads possible
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
            
            {recipe.description && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">{recipe.description}</p>
              </div>
            )}
            
            {recipe.intended_firearms && (() => { try { const p = JSON.parse(recipe.intended_firearms); return Array.isArray(p) && p.length > 0; } catch { return false; } })() && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-1">Intended for:</p>
                <div className="text-sm text-gray-600">
                    {(() => {
                      let arr = [];
                      try {
                        const parsed = JSON.parse(recipe.intended_firearms);
                        arr = Array.isArray(parsed) ? parsed : [];
                      } catch {
                        arr = [];
                      }
                      return arr.map(firearmId => {
                    const firearm = firearms.find(f => f.id === parseInt(firearmId));
                    return firearm ? `${firearm.manufacturer} ${firearm.model}` : null;
                      }).filter(Boolean).join(', ');
                    })()}
                </div>
              </div>
            )}
            
            <div className="mt-4 text-sm text-gray-500">
              Created {new Date(recipe.created_at).toLocaleDateString()}
            </div>

            {/* Favorite toggle bottom-right */}
            <div className="absolute bottom-3 right-3">
              <button
                onClick={(e) => toggleFavorite(e, recipe)}
                className="btn btn-secondary btn-sm"
                title={recipe.is_favorite ? 'Unfavorite' : 'Favorite'}
              >
                <Heart
                  className={recipe.is_favorite ? 'h-4 w-4 text-red-600' : 'h-4 w-4 text-white'}
                  fill={recipe.is_favorite ? 'currentColor' : 'none'}
                />
              </button>
            </div>
          </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        
        {sortedRecipes.length === 0 && (
          <div className="col-span-full text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {recipes.length === 0 ? 'No recipes created yet' : 'No recipes match your filters'}
            </p>
            <button
              onClick={() => { try { reset({ crimp_strength: 1 }); } catch {}; setFieldsLocked(false); setShowForm(true); }}
              className="btn btn-primary mt-4"
            >
              {recipes.length === 0 ? 'Create Your First Recipe' : 'Create New Recipe'}
            </button>
          </div>
        )}
      </div>

      {/* Recipe Detail Modal */}
      <RecipeModal
        show={!!(showRecipeModal && selectedRecipe)}
        recipe={selectedRecipe}
        onClose={closeRecipeModal}
        onEdit={() => handleEdit(selectedRecipe)}
        onImagesUpdated={(recipeId, files) => setImagesByRecipe(prev => ({ ...prev, [recipeId]: Array.isArray(files) ? files : [] }))}
        onLoadsUpdated={async (recipeId) => {
          try {
            const loadsRes = await loadsAPI.getAll();
            const newLoads = Array.isArray(loadsRes.data) ? loadsRes.data : [];
            setLoads(newLoads);
            
            // Incremental stats update for the affected recipe only
            const targetRecipeId = recipeId || selectedRecipe?.id;
            if (targetRecipeId) {
              const recipeLoads = newLoads.filter(l => {
                const lrid = (l && l.recipe && l.recipe.id) || l?.recipe_id;
                return String(lrid) === String(targetRecipeId);
              });
              const totalLoadsCreated = recipeLoads.reduce((sum, load) => sum + (Number(load.quantity_created) || 0), 0);
              const totalLoadsRemaining = recipeLoads.reduce((sum, load) => sum + (Number(load.remaining_quantity) || 0), 0);
              const totalSpent = recipeLoads.reduce((sum, load) => {
                const tc = Number(load.total_cost);
                if (!isNaN(tc) && tc > 0) return sum + tc;
                const cpr = Number(load.cost_per_round);
                const qty = Number(load.quantity_created);
                if (!isNaN(cpr) && !isNaN(qty)) return sum + (cpr * qty);
                return sum;
              }, 0);
              const averageCostPerLoad = totalLoadsCreated > 0 ? totalSpent / totalLoadsCreated : null;
              
              setRecipeStats(prev => ({
                ...prev,
                [targetRecipeId]: {
                  ...prev[targetRecipeId],
                  totalLoadsCreated,
                  totalLoadsRemaining,
                  totalSpent,
                  averageCostPerLoad,
                }
              }));
            }
          } catch {}
        }}
        velocityStatsMap={velocityStats}
        // Pass pre-fetched data to avoid redundant API calls
        initialLoads={loads}
        initialFirearms={firearms}
        initialSessions={sessions}
        initialImages={selectedRecipe ? imagesByRecipe[selectedRecipe.id] : []}
      />
      <ImagePreviewModal id={filePreview?.id} url={filePreview?.url} filename={filePreview?.filename} thumbnailUrl={filePreview?.thumbnailUrl} onClose={() => setFilePreview(null)} />
      {false && showRecipeModal && selectedRecipe && (
        <div className="hidden">
          <div>
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{selectedRecipe.is_deleted ? `Deleted — ${selectedRecipe.name}` : selectedRecipe.name}</h2>
                <button
                  onClick={closeRecipeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {!selectedRecipe.is_deleted && (
                <div className="mb-4 flex gap-3">
                  <button onClick={openAddLoadsFromRecipe} className="btn btn-primary btn-sm">Add Loads</button>
                  <button onClick={openSessionFromRecipe} className="btn btn-secondary btn-sm">Firing Session</button>
                  <button onClick={() => setShowShareRecipe(true)} className="btn btn-secondary btn-sm">Share Recipe</button>
                  <button onClick={() => handleEdit(selectedRecipe)} className="btn btn-secondary btn-sm">Edit</button>
                </div>
              )}
              
              <div className="space-y-4">
                {/* Images */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="btn btn-secondary btn-sm cursor-pointer">
                      Upload Files
                      <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={(e) => handleUploadRecipe(selectedRecipe.id, e.target.files)} />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {(imagesByRecipe[selectedRecipe.id] || []).map(file => {
                      const src = file.download_url || file.url;
                      const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                      return (
                        <div key={file.id} className="relative group">
                          {isPdf ? (
                            <PdfThumbnail
                              fileId={file.id}
                              url={src}
                              thumbnailUrl={file.thumbnail_url}
                              filename={file.filename}
                              sizeClass="w-40 h-56"
                              onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                            />
                          ) : (
                            (() => {
                              const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                              const thumb = isImg ? src : (file.thumbnail_url || src);
                              return (
                                <img src={thumb} alt={file.filename} className="w-24 h-24 object-contain bg-white rounded shadow cursor-pointer" onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })} />
                              );
                            })()
                          )}
                          <div className="flex justify-between mt-1 text-xs text-gray-700">
                            <button className="underline" onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })}>Open</button>
                            <button className="text-red-600 underline" onClick={() => removeRecipeImage(selectedRecipe.id, file.id)}>Delete</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Cartridge</label>
                    <p className="text-sm text-gray-900">{selectedRecipe.cartridge || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Created</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedRecipe.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Recipe Statistics */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-3">Recipe Statistics</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Loads Created:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {getTotalLoadsCreated(selectedRecipe.id)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Loads Remaining:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {getRemainingLoadsForRecipe(selectedRecipe.id)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Spent:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        ${getTotalSpent(selectedRecipe.id).toFixed(2)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Average Cost per Load:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(() => {
                          const stats = recipeStats[selectedRecipe.id] || {};
                          if (stats.costPerRound === null) return 'NA';
                          const avg = getAverageCostPerLoad(selectedRecipe.id);
                          if (avg === null) return 'NA';
                          return `$${Number(avg).toFixed(2)}`;
                        })()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Current Cost per Load:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(() => {
                          const s = recipeStats[selectedRecipe.id] || {};
                          return s.currentCostPerRound != null ? `$${Number(s.currentCostPerRound).toFixed(2)}` : 'NA';
                        })()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Spent:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {(() => {
                          const s = recipeStats[selectedRecipe.id] || {};
                          return `$${Number(s.totalSpent || 0).toFixed(2)}`;
                        })()}
                      </span>
                    </div>
                  </div>
                  {/* Velocity by Firearm (if any) */}
                  {(() => {
                    const per = getVelocityByFirearmForRecipe(selectedRecipe.id) || [];
                    if (per.length === 0) return null;
                    return (
                      <div className="mt-3">
                        <div className="text-sm font-medium text-gray-700 mb-1">Velocity by Firearm</div>
                        <div className="space-y-1 text-xs">
                          {per.map((r, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span className="text-gray-700 truncate pr-2">{r.label}</span>
                              <span className="text-gray-900">Avg {r.average} • ES {r.es ?? '-'} • SD {r.sd ?? '-'} • N {r.count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Load Batches for this Recipe */}
                {(() => {
                  const recipeLoads = (loads || []).filter(l => (l.recipe && l.recipe.id) === selectedRecipe.id);
                  return (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-700">Load Batches</div>
                        <div className="text-xs text-gray-500">{recipeLoads.length} batch{recipeLoads.length === 1 ? '' : 'es'}</div>
                      </div>
                      <div className="space-y-2">
                        {recipeLoads.map((l) => (
                          <div key={l.id} className="flex justify-between items-center text-sm bg-white border rounded p-2">
                            <div>
                              <div className="font-medium text-gray-900">{l.recipe?.name || 'Recipe'}</div>
                              <div className="text-gray-600">
                                {(l.case_batch && typeof l.case_batch.firing_count !== 'undefined') ? `${l.case_batch.firing_count}x brass` : 'New brass'}
                                {l.date_created ? ` • Created ${formatLocalDate(l.date_created)}` : ''}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-700">
                                {Number(l.quantity_created || 0).toLocaleString()} created, {(Number(l.remaining_quantity || 0)).toLocaleString()} remaining
                              </div>
                              <div className="text-gray-600">
                                {(() => {
                                  const tc = Number(l.total_cost);
                                  const qty = Number(l.quantity_created);
                                  // Average cost per load for this card/batch
                                  const avg = (!isNaN(tc) && !isNaN(qty) && qty > 0) ? (tc / qty) : (typeof l.computed_cost_per_round === 'number' ? Number(l.computed_cost_per_round) : Number(l.cost_per_round || 0));
                                  const showTc = (!isNaN(tc) ? tc : (avg * (qty || 0)));
                                  return `$${avg.toFixed(2)}/rd • $${showTc.toFixed(2)} total`;
                                })()}
                              </div>
                            </div>
                          </div>
                        ))}
                        {recipeLoads.length === 0 && (
                          <div className="text-sm text-gray-500">No load batches yet for this recipe.</div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Description */}
                {selectedRecipe.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Description</label>
                    <p className="text-sm text-gray-900">{selectedRecipe.description}</p>
                  </div>
                )}

                {/* Components */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Components</label>
                  
                  {/* Component Summary */}
                  {!loadingAvailability && componentAvailability.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-sm font-medium text-blue-900 mb-2">Component Availability Summary</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {componentAvailability.map((comp, index) => {
                          const recipeComponent = selectedRecipe.components.find(rc => 
                            comp.component.toLowerCase().includes(rc.component.brand.toLowerCase()) &&
                            comp.component.toLowerCase().includes(rc.component.model.toLowerCase())
                          );
                          const maxLoads = recipeComponent ? Math.floor(comp.available_quantity / recipeComponent.quantity) : 0;
                          
                          return (
                            <div key={index} className="flex justify-between">
                              <span className="text-blue-800">{comp.component}:</span>
                              <span className={`font-medium ${
                                maxLoads > 50 ? 'text-green-600' : 
                                maxLoads > 0 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {maxLoads} loads possible
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {loadingAvailability ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-gray-600">Loading component availability...</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedRecipe.components?.map((rc, index) => {
                        const availability = componentAvailability.find(comp => 
                          comp.component.toLowerCase().includes(rc.component.brand.toLowerCase()) &&
                          comp.component.toLowerCase().includes(rc.component.model.toLowerCase())
                        );
                        
                        const maxLoadsPossible = availability ? Math.floor(availability.available_quantity / rc.quantity) : 0;
                        
                        return (
                          <div key={index} className="p-3 bg-gray-50 rounded">
                            <div className="space-y-2">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 text-lg flex items-center justify-between">
                                    <span>{rc.component.brand} {rc.component.model}</span>
                                    <button className="btn btn-secondary btn-xs" title="Purchase" onClick={() => { setPurchaseComponent(rc.component); setShowPurchaseModal(true); setPurchaseForm({ quantity:'', total_cost:'', unit_cost:'', purchase_date:new Date().toISOString().split('T')[0], purchase_location:'', website:'', notes:''}); }}>
                                      <ShoppingCart className="h-4 w-4 text-white" />
                                    </button>
                                  </div>
                                  <div className={`text-sm uppercase tracking-wide font-medium ${
                                    rc.component_type.toLowerCase() === 'bullet' ? 'text-blue-700' :
                                    rc.component_type.toLowerCase() === 'primer' ? 'text-red-700' :
                                    rc.component_type.toLowerCase() === 'powder' ? 'text-green-700' :
                                    rc.component_type.toLowerCase() === 'brass' ? 'text-yellow-700' :
                                    'text-gray-500'
                                  }`}>
                                    {rc.component_type}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Quantity per load: {rc.quantity} {rc.unit}
                                  </div>
                                  
                                  {(() => {
                                    const s = recipeStats[selectedRecipe.id] || {};
                                    const list = Array.isArray(s.componentBreakdown) ? s.componentBreakdown : [];
                                    const find = list.find(cb => {
                                      try {
                                        return (cb.type || '').toLowerCase() === rc.component_type.toLowerCase() && (cb.component || '').toLowerCase().includes(rc.component.brand.toLowerCase()) && (cb.component || '').toLowerCase().includes(rc.component.model.toLowerCase());
                                      } catch {
                                        return false;
                                      }
                                    });
                                    if (!find) return null;
                                    const curUnit = (typeof find.current_unit_cost === 'number') ? find.current_unit_cost : null;
                                    const avgUnit = (typeof find.average_unit_cost === 'number') ? find.average_unit_cost : null;
                                    const qty = Number(rc.quantity || 0);
                                    const curPerLoad = curUnit != null ? (curUnit * qty) : null;
                                    const avgPerLoad = avgUnit != null ? (avgUnit * qty) : null;
                                    return (
                                      <div className="mt-1 text-xs text-gray-700">
                                        <div>Current cost per load: {curPerLoad != null ? `$${curPerLoad.toFixed(2)}` : 'NA'}</div>
                                        <div>Average cost per load: {avgPerLoad != null ? `$${avgPerLoad.toFixed(2)}` : 'NA'}</div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                              
                              {availability && (
                                <div className="pt-2 border-t border-gray-200">
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-600">Available:</span>
                                      <span className="ml-1 font-medium text-gray-900">
                                        {availability.available_quantity.toLocaleString()} {availability.unit}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Max loads:</span>
                                      <span className={`ml-1 font-medium ${maxLoadsPossible > 50 ? 'text-green-600' : maxLoadsPossible > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                                        {maxLoadsPossible.toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                  {rc.component_type.toLowerCase() === 'powder' && availability.unit === 'grains' && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      ({(availability.available_quantity / 7000).toFixed(2)} lbs available)
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Dimensions */}
                {(selectedRecipe.case_trim_length || selectedRecipe.cartridge_overall_length || selectedRecipe.seating_depth) && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Dimensions</label>
                    <div className="grid grid-cols-3 gap-4">
                      {selectedRecipe.case_trim_length && (
                        <div>
                          <label className="text-xs text-gray-600">Case Trim Length</label>
                          <p className="text-sm text-gray-900">{selectedRecipe.case_trim_length}"</p>
                        </div>
                      )}
                      {selectedRecipe.cartridge_overall_length && (
                        <div>
                          <label className="text-xs text-gray-600">Overall Length</label>
                          <p className="text-sm text-gray-900">{selectedRecipe.cartridge_overall_length}"</p>
                        </div>
                      )}
                      {selectedRecipe.seating_depth && (
                        <div>
                          <label className="text-xs text-gray-600">Seating Depth</label>
                          <p className="text-sm text-gray-900">{selectedRecipe.seating_depth}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Crimp Settings */}
                {(selectedRecipe.crimp_type || selectedRecipe.crimp_strength) && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">Crimp Settings</label>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedRecipe.crimp_type && (
                        <div>
                          <label className="text-xs text-gray-600">Crimp Type</label>
                          <p className="text-sm text-gray-900">{selectedRecipe.crimp_type}</p>
                        </div>
                      )}
                      {selectedRecipe.crimp_strength && (
                        <div>
                          <label className="text-xs text-gray-600">Crimp Strength</label>
                          <p className="text-sm text-gray-900">{selectedRecipe.crimp_strength}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedRecipe.notes && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Notes</label>
                    <p className="text-sm text-gray-900">{selectedRecipe.notes}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>
                {(selectedRecipe.load_counts?.remaining ?? 0).toLocaleString()} rem · {(selectedRecipe.load_counts?.total ?? 0).toLocaleString()} total
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Add Loads From Recipe Modal */}
      {showAddLoadsFromRecipe && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Loads - {selectedRecipe.name}</h3>
              <button onClick={() => setShowAddLoadsFromRecipe(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <form onSubmit={handleSubmitAddLoad(onSubmitAddLoadsFromRecipe)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Recipe</label>
                <input value={selectedRecipe.name} readOnly className="input bg-gray-100" />
              </div>
              <div className="md:col-span-2">
                <label className="label">Case Batch (Required)</label>
                <select {...registerAddLoad('case_batch_id', { required: 'Case batch is required' })} className="input" defaultValue="">
                  <option value="">Select case batch...</option>
                  {recipeCaseBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {(batch.head_stamp || batch.component_name)} - {batch.firing_count} firings ({batch.remaining_quantity} remaining){Array.isArray(batch.batch_ids) && batch.batch_ids.length > 1 ? ` • pooled (${batch.batch_ids.length})` : ''}
                    </option>
                  ))}
                </select>
                {errorsAddLoad.case_batch_id && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.case_batch_id.message}</p>)}
              </div>
              <div>
                <label className="label">Quantity to Create</label>
                <input type="number" className="input" placeholder="e.g., 50" {...registerAddLoad('quantity_created', { required: 'Quantity is required', min: { value: 1, message: 'Must be at least 1' } })} />
                {errorsAddLoad.quantity_created && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.quantity_created.message}</p>)}
              </div>
              <div>
                <label className="label">Date Created</label>
                <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerAddLoad('date_created', { required: 'Date is required' })} />
                {errorsAddLoad.date_created && (<p className="text-red-500 text-sm mt-1">{errorsAddLoad.date_created.message}</p>)}
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" placeholder="Notes (optional)" {...registerAddLoad('notes')} />
              </div>
              
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn btn-primary">Create Load Batch</button>
                <button type="button" onClick={() => setShowAddLoadsFromRecipe(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Firing Session From Recipe Modal */}
      {showSessionFromRecipe && selectedRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">New Firing Session - {selectedRecipe.name}</h3>
              <button onClick={() => setShowSessionFromRecipe(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <form onSubmit={handleSubmitSession(onSubmitSessionFromRecipe)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Select Load Batch (with remaining rounds)</label>
                <select className="input" defaultValue="" {...registerSession('load_id', { required: 'Load batch is required' })}>
                  <option value="">Select load batch...</option>
                  {loads.filter(l => l.recipe?.id === selectedRecipe.id && (l.remaining_quantity || 0) > 0).map(l => (
                    <option key={l.id} value={l.id}>
                      {l.recipe.name} • {l.remaining_quantity} remaining • {l.case_batch ? `${l.case_batch.firing_count}x brass` : 'new brass'}
                    </option>
                  ))}
                </select>
                {errorsSession.load_id && (<p className="text-red-500 text-sm mt-1">{errorsSession.load_id.message}</p>)}
              </div>
              <div>
                <label className="label">Firearm</label>
                <select className="input" defaultValue={selectedRecipe.firearm?.id || ''} {...registerSession('firearm_id', { required: 'Firearm is required' })}>
                  <option value="">Select firearm...</option>
                  {getCompatibleFirearmsForRecipe(selectedRecipe).map(f => (
                    <option key={f.id} value={f.id}>{f.manufacturer} {f.model} ({f.caliber})</option>
                  ))}
                </select>
                {errorsSession.firearm_id && (<p className="text-red-500 text-sm mt-1">{errorsSession.firearm_id.message}</p>)}
              </div>
              <div>
                <label className="label">Location</label>
                <input type="text" className="input" placeholder="e.g., Local Range" {...registerSession('location', { required: 'Location is required' })} />
                {errorsSession.location && (<p className="text-red-500 text-sm mt-1">{errorsSession.location.message}</p>)}
              </div>
              <div>
                <label className="label">Date</label>
                <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerSession('session_date', { required: 'Date is required' })} />
                {errorsSession.session_date && (<p className="text-red-500 text-sm mt-1">{errorsSession.session_date.message}</p>)}
              </div>
              <div>
                <label className="label">Rounds Fired</label>
                <input type="number" className="input" placeholder="e.g., 10" {...registerSession('rounds_fired', { required: 'Rounds fired is required', min: { value: 1, message: 'Must be at least 1' } })} />
                {errorsSession.rounds_fired && (<p className="text-red-500 text-sm mt-1">{errorsSession.rounds_fired.message}</p>)}
              </div>
              <div>
                <label className="label">Temperature (°F)</label>
                <input type="number" className="input" placeholder="e.g., 72" {...registerSession('temperature')} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" placeholder="Session notes (optional)" {...registerSession('notes')} />
              </div>
              
              

              {/* Velocity Records (fps) */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label">Velocity Records (fps)</label>
                  <button type="button" onClick={() => appendSessionVelocity({ velocity: '' })} className="btn btn-sm btn-primary">
                    <span className="mr-1">+</span> Add Velocity
                  </button>
                </div>
                {fieldsSession.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                    {fieldsSession.map((field, index) => (
                      <div key={field.id} className="relative">
                        <input type="number" {...registerSession(`velocities.${index}.velocity`)} className="input text-sm pr-8" placeholder={`Shot ${index + 1}`} />
                        <button type="button" onClick={() => removeSessionVelocity(index)} className="absolute right-1 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700" title="Remove this velocity">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500">Click "Add Velocity" to record individual shot velocities (optional)</p>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn btn-primary">Record Session</button>
                <button type="button" onClick={() => setShowSessionFromRecipe(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Recipe Modal */}
      {showShareRecipe && selectedRecipe && (
        <ShareRecipeModal onClose={() => setShowShareRecipe(false)} recipeId={selectedRecipe.id} />
      )}

      {/* Edit Recipe Modal */}
      {showEditModal && editingRecipe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit {editingRecipe.name}</h2>
                <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Recipe Name</label>
                    <input
                      type="text"
                      {...register('name', { required: 'Recipe name is required' })}
                      className="input"
                      placeholder="e.g., .357 Magnum Target Load"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Cartridge</label>
                    <div>
                      <select
                        {...register('cartridge', { required: 'Cartridge is required' })}
                        className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        disabled={fieldsLocked}
                      >
                        <option value="">Select cartridge...</option>
                        {cartridges.map(cartridge => (
                          <option key={cartridge.id} value={cartridge.value}>
                            {cartridge.value}
                          </option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                    </div>
                    {errors.cartridge && (
                      <p className="text-red-500 text-sm mt-1">{errors.cartridge.message}</p>
                    )}
                  </div>
                </div>

                {/* Custom Cartridge */}
                {cartridge === 'other' && (
                  <div>
                    <label className="label">Custom Cartridge</label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        {...register('cartridge_other', { required: 'Custom cartridge name is required' })}
                        className={`input flex-1 ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder="Enter cartridge name..."
                        disabled={fieldsLocked}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const customCartridge = watch('cartridge_other');
                          if (customCartridge) {
                            addCustomCartridge(customCartridge);
                          }
                        }}
                        className={`btn btn-secondary ${fieldsLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={fieldsLocked}
                      >
                        Add
                      </button>
                    </div>
                    {errors.cartridge_other && (
                      <p className="text-red-500 text-sm mt-1">{errors.cartridge_other.message}</p>
                    )}
                  </div>
                )}

                {/* Component Selection (cartridge must be selected first) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Brass Selection */}
                  <div>
                    <label className="label">Brass</label>
                    <select
                      {...register('brass_id', { required: 'Brass is required' })}
                      className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!effectiveCartridge || fieldsLocked}
                    >
                      <option value="">Select brass...</option>
                      {componentsByType.brass
                        ?.filter(brass => effectiveCartridge && (brass.caliber || '').toLowerCase() === effectiveCartridge.toLowerCase())
                        .map(brass => (
                        <option key={brass.id} value={brass.id}>
                          {brass.head_stamp || brass.model || brass.brand}
                        </option>
                      ))}
                    </select>
                    {errors.brass_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.brass_id.message}</p>
                    )}
                  </div>

                  {/* Bullet Selection */}
                  <div>
                    <label className="label">Bullet</label>
                    <select
                      {...register('bullet_id', { required: 'Bullet is required' })}
                      className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!effectiveCartridge || fieldsLocked}
                    >
                      <option value="">Select bullet...</option>
                      {componentsByType.bullet
                        ?.filter(bullet => {
                          try {
                            const arr = bullet.intended_cartridges ? JSON.parse(bullet.intended_cartridges) : [];
                            return effectiveCartridge && arr.map(v => (v || '').toString().toLowerCase()).includes(effectiveCartridge.toLowerCase());
                          } catch { return false; }
                        })
                        .map(bullet => (
                        <option key={bullet.id} value={bullet.id}>
                          {bullet.brand} {bullet.model} ({bullet.weight_grains}gr)
                        </option>
                      ))}
                    </select>
                    {errors.bullet_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.bullet_id.message}</p>
                    )}
                  </div>

                  {/* Primer Selection */}
                  <div>
                    <label className="label">Primer</label>
                    <select
                      {...register('primer_id', { required: 'Primer is required' })}
                      className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!selectedBrass || fieldsLocked}
                    >
                      <option value="">Select primer...</option>
                      {(() => {
                        if (!selectedBrass) return null;
                        const brass = componentsByType.brass?.find(b => b.id === parseInt(selectedBrass));
                        if (!brass) return null;
                        const size = (brass.primer_pocket_size || '').toLowerCase();
                        const type = (brass.primer_pocket_type || '').toLowerCase();
                        return componentsByType.primer
                          ?.filter(primer => {
                            const pSize = (primer.primer_size || '').toLowerCase();
                            const pType = (primer.firearm_type || '').toLowerCase();
                            if (!size) return false;
                            if (size === 'small') {
                              return pSize === 'small';
                            }
                            return pSize === 'large' && (!!type ? pType === type : true);
                          })
                          .map(primer => (
                            <option key={primer.id} value={primer.id}>
                              {primer.brand} {primer.model}
                            </option>
                          ));
                      })()}
                    </select>
                    {errors.primer_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.primer_id.message}</p>
                    )}
                  </div>

                  {/* Powder Selection */}
                  <div>
                    <label className="label">Powder</label>
                    <select
                      {...register('powder_id', { required: 'Powder is required' })}
                      className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={fieldsLocked}
                    >
                      <option value="">Select powder...</option>
                      {componentsByType.powder?.map(powder => (
                        <option key={powder.id} value={powder.id}>
                          {powder.brand} {powder.model}
                        </option>
                      ))}
                    </select>
                    {errors.powder_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.powder_id.message}</p>
                    )}
                  </div>
                </div>

                {/* Powder Charge */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="label">Powder Charge (grains)</label>
                    <input
                      type="number"
                      step="0.1"
                      {...register('powder_charge', { 
                        required: 'Powder charge is required',
                        min: { value: 0, message: 'Charge must be positive' },
                        max: { value: 999.9, message: 'Charge must be less than 1000' }
                      })}
                      className={`input ${fieldsLocked ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={fieldsLocked}
                      placeholder="e.g., 5.5"
                    />
                    {errors.powder_charge && (
                      <p className="text-red-500 text-sm mt-1">{errors.powder_charge.message}</p>
                    )}
                  </div>
                </div>

                {/* Self-Cannelured Checkbox */}
                {selectedBullet && !getSelectedBullet()?.cannelured && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('self_cannelured')}
                      className="checkbox"
                    />
                    <label className="label">Self-Cannelured</label>
                  </div>
                )}

                {/* Cannelure Depth */}
                {(getSelectedBullet()?.cannelured || selfCannelured) && (
                  <div>
                    <label className="label">Cannelure Depth (inches)</label>
                    <input
                      type="number"
                      step="0.001"
                      {...register('cannelure_depth')}
                      className="input"
                      placeholder="e.g., 0.010"
                      readOnly={getSelectedBullet()?.cannelured && getSelectedBullet()?.cannelure_depth}
                      title={getSelectedBullet()?.cannelured && getSelectedBullet()?.cannelure_depth ? "Auto-populated from bullet data" : ""}
                    />
                    {getSelectedBullet()?.cannelured && getSelectedBullet()?.cannelure_depth && (
                      <p className="text-sm text-gray-600 mt-1">Auto-populated from bullet data</p>
                    )}
                  </div>
                )}

                {/* Dimension Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {selectedBullet && (
                    <div className="col-span-full">
                      <p className="text-sm text-blue-600 mb-2">
                        <Calculator className="inline h-4 w-4 mr-1" />
                        Enter any two dimensions and the third will be calculated automatically using the selected bullet's length.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="label">Case Trim Length (inches)</label>
                    <input
                      type="number"
                      step="0.001"
                      {...register('case_trim_length')}
                      className="input"
                      placeholder="e.g., 1.280"
                    />
                  </div>

                  <div>
                    <label className="label">Cartridge Overall Length (inches)</label>
                    <input
                      type="number"
                      step="0.001"
                      {...register('cartridge_overall_length')}
                      className="input"
                      placeholder="e.g., 1.590"
                    />
                  </div>

                  <div>
                    <label className="label">Seating Depth (inches)</label>
                    <input
                      type="number"
                      step="0.001"
                      {...register('seating_depth')}
                      className="input"
                      placeholder="e.g., 0.200"
                    />
                  </div>
                </div>

                {/* Crimp Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Crimp Type</label>
                    <select {...register('crimp_type')} className="input">
                      <option value="">Select crimp type...</option>
                      <option value="Roll">Roll</option>
                      <option value="Taper">Taper</option>
                      <option value="Collet">Collet</option>
                    </select>
                  </div>

                  <div>
                    <label className="label">Crimp Strength (Die Turns)</label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      {...register('crimp_strength')}
                      className="w-full"
                    />
                    <div className="text-sm text-gray-600 mt-1">
                      Value: {watch('crimp_strength') || '0'}
                    </div>
                  </div>
                </div>

                {/* Intended Firearms */}
                <div>
                  <label className="label">Intended Firearms</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {firearms.map(firearm => (
                      <label key={firearm.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          value={firearm.id}
                          {...register('intended_firearms')}
                          className="checkbox"
                        />
                        <span className="text-sm">
                          {firearm.manufacturer} {firearm.model}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Description and Notes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Description</label>
                    <textarea
                      {...register('description')}
                      className="input"
                      rows="3"
                      placeholder="Brief description of this recipe..."
                    />
                  </div>

                  <div>
                    <label className="label">Notes</label>
                    <textarea
                      {...register('notes')}
                      className="input"
                      rows="3"
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary">
                    Update Recipe
                  </button>
                  <button type="button" onClick={handleCancel} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recipes;

// Share modal component
const ShareRecipeModal = ({ onClose, recipeId }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  const [message, setMessage] = useState('Check out this recipe!');

  const loadUsers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.listVisible('');
      const myId = JSON.parse(localStorage.getItem('authUser') || '{}')?.id;
      const list = (res.data || []).filter(u => u.id !== myId);
      setUsers(list);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const send = async () => {
    try {
      const ids = Object.keys(selected).filter(id => selected[id]).map(id => Number(id));
      if (ids.length === 0) return;
      if (ids.length === 1) {
        try {
          const up = await conversationsAPI.upsert([ids[0]]);
          const convId = up?.data?.id;
          if (convId) await conversationsAPI.sendMessage(convId, message, undefined, recipeId);
        } catch {}
      } else {
        try {
          // Create one group conversation with all recipients and include the recipe_id in the first message
          await conversationsAPI.create(ids, message, recipeId);
        } catch {}
      }
      toast.success('Recipe shared');
      onClose();
    } catch {
      toast.error('Failed to share recipe');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Share Recipe</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">×</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">Message</label>
            <textarea className="input h-24" value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-2">Select recipients</div>
            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {users.map(u => (
                  <label key={u.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!selected[u.id]} onChange={() => toggle(u.id)} />
                    <span>{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</span>
                    <span className="text-gray-500">({u.email})</span>
                  </label>
                ))}
                {users.length === 0 && (
                  <div className="text-sm text-gray-500">No visible users to share with.</div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={send}>Send</button>
          </div>
        </div>
      </div>
    </div>
  );
};